import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Shield,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  XCircle,
  X,
  Play,
  RefreshCw,
  Download,
  Clock,
  FileSearch,
  Key,
  Package,
  Server,
  Settings,
  FileText,
  TrendingUp,
  TrendingDown,
  Activity,
  Eye,
  Filter,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Copy,
  Check,
  Info,
  Loader2
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

// Types
interface ToolStatus {
  name: string;
  command: string;
  category: string;
  description: string;
  isInstalled: boolean;
  version: string | null;
  url?: string;
  vendor?: string;
  detailedDescription?: string;
  whatItChecks?: string[];
  howItWorks?: string;
  standards?: string[];
}

interface ToolDocumentation {
  name: string;
  command: string;
  category: string;
  description: string;
  url: string;
  vendor: string;
  detailedDescription: string;
  whatItChecks: string[];
  howItWorks: string;
  standards: string[];
}

interface ScanMethodology {
  overview: string;
  process: {
    step: number;
    title: string;
    description: string;
    tools: string[];
    coverage: string;
  }[];
  scoring: {
    formula: string;
    description: string;
    grading: {
      grade: string;
      range: string;
      status: string;
      description: string;
    }[];
  };
  dataRetention: string;
  frequency: string;
}

interface ScanProgress {
  currentTool: string;
  completedTools: string[];
  totalTools: number;
  percentage: number;
}

interface ScanSummary {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
  byCategory: Record<string, number>;
  byTool: Record<string, number>;
}

interface Scan {
  id: number;
  scan_id: string;
  scan_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  started_at: string | null;
  completed_at: string | null;
  total_findings: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  security_score: number;
  scan_duration_seconds: number | null;
  error_message: string | null;
}

interface StandardImpact {
  standard: string;
  code: string;
  name: string;
}

interface ScanResult {
  id: number;
  scan_id: number;
  category: string;
  tool: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  rule_id: string | null;
  title: string;
  description: string | null;
  file_path: string | null;
  line_number: number | null;
  package_name: string | null;
  package_version: string | null;
  fixed_version: string | null;
  cve_id: string | null;
  remediation: string | null;
  remediation_command: string | null;
  status: 'open' | 'in_progress' | 'fixed' | 'ignored' | 'false_positive';
  references: string[];
  standards_impacted?: StandardImpact[];
}

interface DashboardData {
  latestScan: Scan | null;
  breakdown: ScanSummary | null;
  securityScore: number;
  openIssues: number;
  fixedIssues: number;
  inProgressIssues: number;
  ignoredIssues: number;
  falsePositiveIssues: number;
  openCriticalCount: number;
  openHighCount: number;
  trends: Scan[];
  tools: Record<string, ToolStatus>;
  recentScans: Scan[];
}

interface ComplianceItem {
  status: 'compliant' | 'partial' | 'non-compliant';
  score: number;
  findings: Record<string, number>;
  notes: string;
  details?: Array<{
    id?: string;
    code?: string;
    name?: string;
    description?: string;
    status?: string;
    result?: string;
    count?: number;
  }>;
}

interface SecurityStandard {
  name: string;
  version: string;
  description: string;
  url: string;
  categories: string[];
}

interface ComplianceData {
  standards: Record<string, SecurityStandard>;
  compliance: Record<string, ComplianceItem>;
  summary?: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    secrets: number;
    dependencies: number;
  };
  noScanData?: boolean;
}

// Severity badge component
const SeverityBadge: React.FC<{ severity: string; count?: number }> = ({ severity, count }) => {
  const colors: Record<string, string> = {
    critical: 'bg-red-100 text-red-800 border-red-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    low: 'bg-blue-100 text-blue-800 border-blue-200',
    info: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${colors[severity] || colors.info}`}>
      {severity.toUpperCase()}{count !== undefined ? `: ${count}` : ''}
    </span>
  );
};

// Status badge component
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const configs: Record<string, { color: string; icon: React.ReactNode }> = {
    pending: { color: 'bg-gray-100 text-gray-800', icon: <Clock className="w-3 h-3" /> },
    running: { color: 'bg-blue-100 text-blue-800', icon: <Loader2 className="w-3 h-3 animate-spin" /> },
    completed: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3 h-3" /> },
    failed: { color: 'bg-red-100 text-red-800', icon: <XCircle className="w-3 h-3" /> },
    cancelled: { color: 'bg-gray-100 text-gray-800', icon: <XCircle className="w-3 h-3" /> },
    open: { color: 'bg-red-100 text-red-800', icon: <AlertCircle className="w-3 h-3" /> },
    in_progress: { color: 'bg-gray-100 text-gray-700', icon: <Activity className="w-3 h-3" /> },
    fixed: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3 h-3" /> },
    ignored: { color: 'bg-gray-100 text-gray-800', icon: <Eye className="w-3 h-3" /> },
    false_positive: { color: 'bg-purple-100 text-purple-800', icon: <Info className="w-3 h-3" /> }
  };

  const config = configs[status] || configs.pending;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.icon}
      {status.replace('_', ' ').toUpperCase()}
    </span>
  );
};

// Impact badge component for security standards
const ImpactBadge: React.FC<{ impact: StandardImpact }> = ({ impact }) => {
  const colors: Record<string, string> = {
    OWASP: 'bg-red-50 text-red-700 border-red-200',
    CWE: 'bg-orange-50 text-orange-700 border-orange-200',
    PCI_DSS: 'bg-blue-50 text-blue-700 border-blue-200',
    ISO_27001: 'bg-green-50 text-green-700 border-green-200',
    GDPR: 'bg-purple-50 text-purple-700 border-purple-200',
    SOC2: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    NIST: 'bg-cyan-50 text-cyan-700 border-cyan-200'
  };

  const colorClass = colors[impact.standard] || 'bg-gray-50 text-gray-700 border-gray-200';

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${colorClass}`}>
      <span className="font-semibold">{impact.standard} {impact.code}:</span>
      <span className="ml-1">{impact.name}</span>
    </span>
  );
};

// Security Score Gauge
const SecurityScoreGauge: React.FC<{ score: number }> = ({ score: rawScore }) => {
  // Ensure score is a valid number
  const score = Number(rawScore) || 0;

  const getColor = (score: number) => {
    if (score >= 90) return 'text-green-500';
    if (score >= 80) return 'text-green-400';
    if (score >= 70) return 'text-yellow-500';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 50) return 'text-orange-500';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-500';
  };

  const getBgColor = (score: number) => {
    if (score >= 90) return 'bg-green-50';
    if (score >= 80) return 'bg-green-50';
    if (score >= 70) return 'bg-yellow-50';
    if (score >= 60) return 'bg-yellow-50';
    if (score >= 50) return 'bg-orange-50';
    if (score >= 40) return 'bg-orange-50';
    return 'bg-red-50';
  };

  const getGrade = (score: number) => {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  };

  const getLabel = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Good';
    if (score >= 70) return 'Fair';
    if (score >= 60) return 'Needs Work';
    if (score >= 50) return 'Poor';
    if (score >= 40) return 'At Risk';
    return 'Critical';
  };

  const getGradeColor = (score: number) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 80) return 'bg-green-400';
    if (score >= 70) return 'bg-yellow-500';
    if (score >= 60) return 'bg-yellow-600';
    if (score >= 50) return 'bg-orange-500';
    if (score >= 40) return 'bg-orange-600';
    return 'bg-red-500';
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-36 h-36">
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="72"
            cy="72"
            r="60"
            stroke="currentColor"
            strokeWidth="14"
            fill="none"
            className="text-gray-200"
          />
          <circle
            cx="72"
            cy="72"
            r="60"
            stroke="currentColor"
            strokeWidth="14"
            fill="none"
            strokeDasharray={`${score * 3.77} 377`}
            className={getColor(score)}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className={`w-10 h-10 rounded-full ${getGradeColor(score)} flex items-center justify-center mb-1`}>
            <span className="text-xl font-bold text-white">{getGrade(score)}</span>
          </div>
          <span className={`text-2xl font-bold ${getColor(score)}`}>{score.toFixed(1)}</span>
          <span className="text-xs text-gray-500">/ 100</span>
        </div>
      </div>
      <div className={`mt-3 px-3 py-1 rounded-full ${getBgColor(score)}`}>
        <span className={`text-sm font-medium ${getColor(score)}`}>{getLabel(score)}</span>
      </div>
    </div>
  );
};

// Category icon mapping
const getCategoryIcon = (category: string) => {
  const icons: Record<string, React.ReactNode> = {
    sast: <FileSearch className="w-4 h-4" />,
    secrets: <Key className="w-4 h-4" />,
    dependency: <Package className="w-4 h-4" />,
    container: <Server className="w-4 h-4" />,
    infrastructure: <Settings className="w-4 h-4" />,
    license: <FileText className="w-4 h-4" />
  };
  return icons[category] || <AlertCircle className="w-4 h-4" />;
};

// Scan Progress Bar Component
const ScanProgressBar: React.FC<{ progress: ScanProgress }> = ({ progress }) => {
  const toolNames = ['Semgrep', 'Gitleaks', 'npm audit (Backend)', 'npm audit (Frontend)', 'Trivy', 'Checkov', 'License Checker'];

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Scan in Progress</h3>
        <span className="text-sm text-gray-500">{progress.percentage}% Complete</span>
      </div>

      {/* Overall Progress Bar */}
      <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-gradient-to-r from-super-dark-purple to-electric-blue transition-all duration-500 ease-out"
          style={{ width: `${progress.percentage}%` }}
        />
      </div>

      {/* Current Tool */}
      <div className="flex items-center gap-2 mb-4">
        <Loader2 className="w-4 h-4 animate-spin text-electric-blue" />
        <span className="text-sm font-medium text-gray-700">
          Running: {progress.currentTool || 'Initializing...'}
        </span>
      </div>

      {/* Tool Steps */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {toolNames.map((tool, index) => {
          const isCompleted = progress.completedTools.includes(tool);
          const isCurrent = progress.currentTool === tool;

          return (
            <div
              key={tool}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                isCompleted
                  ? 'bg-green-100 text-green-700'
                  : isCurrent
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-500'
              }`}
            >
              {isCompleted ? (
                <CheckCircle className="w-3 h-3" />
              ) : isCurrent ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Clock className="w-3 h-3" />
              )}
              <span className="truncate">{tool.split(' ')[0]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Main Dashboard Component
const SecurityScanDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'results' | 'history' | 'tools'>('overview');
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [complianceData, setComplianceData] = useState<ComplianceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [expandedStandards, setExpandedStandards] = useState<Set<string>>(new Set());
  const [scanType, setScanType] = useState<string>('full');
  const [currentScanId, setCurrentScanId] = useState<number | null>(null);
  const [scanProgress, setScanProgress] = useState<ScanProgress>({
    currentTool: '',
    completedTools: [],
    totalTools: 8,
    percentage: 0
  });
  const [scanResults, setScanResults] = useState<ScanResult[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [selectedScanId, setSelectedScanId] = useState<number | null>(null);
  const [filters, setFilters] = useState({
    severity: '',
    category: '',
    status: 'open',  // Default: show only open findings
    standard: '',  // For filtering by security standard (e.g., "OWASP")
    categoryCode: ''  // For filtering by specific category code (e.g., "A03")
  });
  const [expandedResult, setExpandedResult] = useState<number | null>(null);
  const [copiedCommand, setCopiedCommand] = useState<number | null>(null);
  const [selectedResults, setSelectedResults] = useState<Set<number>>(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Filter scan results by selected standard and category code, exclude non-security findings
  const filteredScanResults = useMemo(() => {
    // First, filter out non-security findings (license compliance checks)
    const securityOnlyResults = scanResults.filter(result => {
      // Exclude license-checker findings from security dashboard
      if (result.tool === 'license-checker' ||
          result.category === 'license' ||
          result.category === 'License Compliance' ||
          result.title?.toLowerCase().includes('license compliance')) {
        return false;
      }
      return true;
    });

    // Apply all filters (status, standard, categoryCode)
    return securityOnlyResults.filter(result => {
      // Filter by status
      if (filters.status && result.status !== filters.status) {
        return false;
      }

      // If no standard/category filters, return after status filter
      if (!filters.standard && !filters.categoryCode) return true;

      if (!result.standards_impacted || result.standards_impacted.length === 0) {
        return false;
      }

      // Check if result matches the standard filter
      const matchesStandard = !filters.standard || result.standards_impacted.some(impact =>
        impact.standard.toLowerCase() === filters.standard.toLowerCase() ||
        filters.standard.toLowerCase().includes(impact.standard.toLowerCase())
      );

      // Check if result matches the category code filter (e.g., "A03", "A05")
      const matchesCategoryCode = !filters.categoryCode || result.standards_impacted.some(impact =>
        impact.code && impact.code.toLowerCase().includes(filters.categoryCode.toLowerCase())
      );

      return matchesStandard && matchesCategoryCode;
    });
  }, [scanResults, filters.standard, filters.categoryCode, filters.status]);

  // Recalculate breakdown and security score based on filtered security-only results
  const filteredBreakdown = useMemo(() => {
    if (!filteredScanResults || filteredScanResults.length === 0) {
      return {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
        info: 0,
        total: 0
      };
    }

    const breakdown = {
      critical: filteredScanResults.filter(r => r.severity === 'critical').length,
      high: filteredScanResults.filter(r => r.severity === 'high').length,
      medium: filteredScanResults.filter(r => r.severity === 'medium').length,
      low: filteredScanResults.filter(r => r.severity === 'low').length,
      info: filteredScanResults.filter(r => r.severity === 'info').length,
      total: filteredScanResults.length
    };

    return breakdown;
  }, [filteredScanResults]);

  // Calculate security score from filtered results
  const filteredSecurityScore = useMemo(() => {
    const { critical, high, medium, low, total } = filteredBreakdown;

    // If no findings, score is 100
    if (total === 0) return 100;

    // Weighted scoring: critical = -10, high = -5, medium = -2, low = -1 per finding
    const deductions = (critical * 10) + (high * 5) + (medium * 2) + (low * 1);
    const score = Math.max(0, 100 - deductions);

    return Math.round(score * 10) / 10; // Round to 1 decimal place
  }, [filteredBreakdown]);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardRes, complianceRes] = await Promise.all([
        api.get('/api/security-scan/dashboard'),
        api.get('/api/security-scan/compliance')
      ]);
      if (dashboardRes.data.success) {
        setDashboardData(dashboardRes.data.dashboard);
        if (dashboardRes.data.dashboard.latestScan) {
          setSelectedScanId(dashboardRes.data.dashboard.latestScan.id);
        }
      }
      if (complianceRes.data.success) {
        setComplianceData(complianceRes.data);
      }
    } catch (error: any) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load security dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch scan results
  const fetchScanResults = useCallback(async (scanId: number) => {
    try {
      setResultsLoading(true);
      setSelectedResults(new Set()); // Clear selection when fetching new results
      const params = new URLSearchParams();
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.category) params.append('category', filters.category);
      if (filters.status) params.append('status', filters.status);

      const response = await api.get(`/api/security-scan/${scanId}/results?${params.toString()}`);
      if (response.data.success) {
        setScanResults(response.data.results);
      }
    } catch (error: any) {
      console.error('Failed to fetch results:', error);
      toast.error('Failed to load scan results');
    } finally {
      setResultsLoading(false);
    }
  }, [filters]);

  // Filter findings by security standard and optionally by specific category code
  const filterByStandard = (standardKey: string, categoryCode?: string) => {
    setFilters(prev => ({
      ...prev,
      standard: standardKey,
      categoryCode: categoryCode || ''
    }));
    setActiveTab('results');
    if (dashboardData?.latestScan && !selectedScanId) {
      setSelectedScanId(dashboardData.latestScan.id);
    }
  };

  // Clear standard and category code filters
  const clearStandardFilter = () => {
    setFilters(prev => ({ ...prev, standard: '', categoryCode: '' }));
  };

  // Simulate scan progress (since backend doesn't provide real-time tool status)
  const simulateProgress = useCallback(() => {
    const tools = ['Semgrep', 'Gitleaks', 'npm audit (Backend)', 'npm audit (Frontend)', 'Trivy', 'Checkov', 'License Checker'];
    let currentIndex = 0;
    const completedTools: string[] = [];

    const interval = setInterval(() => {
      if (currentIndex < tools.length) {
        // Move previous tool to completed
        if (currentIndex > 0) {
          completedTools.push(tools[currentIndex - 1]);
        }

        setScanProgress({
          currentTool: tools[currentIndex],
          completedTools: [...completedTools],
          totalTools: tools.length,
          percentage: Math.round(((currentIndex + 0.5) / tools.length) * 100)
        });

        currentIndex++;
      }
    }, 8000); // Each tool takes ~8 seconds on average

    return () => clearInterval(interval);
  }, []);

  // Poll for scan status
  const pollScanStatus = useCallback(async (scanId: number) => {
    try {
      const response = await api.get(`/api/security-scan/status/${scanId}`);
      if (response.data.success) {
        const scan = response.data.scan;
        if (scan.status === 'completed' || scan.status === 'failed' || scan.status === 'cancelled') {
          setScanning(false);
          setCurrentScanId(null);
          setScanProgress({
            currentTool: '',
            completedTools: [],
            totalTools: 7,
            percentage: 100
          });
          fetchDashboard();
          if (scan.status === 'completed') {
            toast.success(`Scan completed! Found ${scan.total_findings} findings.`);
          } else if (scan.status === 'failed') {
            toast.error(`Scan failed: ${scan.error_message || 'Unknown error'}`);
          }
        }
      }
    } catch (error) {
      console.error('Failed to poll scan status:', error);
    }
  }, [fetchDashboard]);

  // Start a new scan
  const startScan = async () => {
    try {
      setScanning(true);
      setScanProgress({
        currentTool: 'Initializing...',
        completedTools: [],
        totalTools: 7,
        percentage: 0
      });
      const response = await api.post('/api/security-scan/run', { scanType });
      if (response.data.success) {
        setCurrentScanId(response.data.scan.id);
        toast.success('Security scan started');
        // Start progress simulation
        simulateProgress();
      }
    } catch (error: any) {
      console.error('Failed to start scan:', error);
      toast.error('Failed to start security scan');
      setScanning(false);
    }
  };

  // Export scan report as JSON
  const exportReportJSON = async (scanId: number) => {
    try {
      const response = await api.get(`/api/security-scan/export/${scanId}`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `security-scan-${scanId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('JSON Report downloaded');
    } catch (error: any) {
      console.error('Failed to export report:', error);
      toast.error('Failed to export report');
    }
  };

  // Export scan report as PDF
  const exportReportPDF = async (scanId: number) => {
    try {
      const response = await api.get(`/api/security-scan/export/${scanId}`);
      const report = response.data;

      const score = Number(report.securityScore) || 0;
      const grade = score >= 90 ? 'A' : score >= 80 ? 'B' : score >= 70 ? 'C' : score >= 60 ? 'D' : 'F';
      const gradeColor = score >= 90 ? '#10b981' : score >= 80 ? '#22c55e' : score >= 70 ? '#eab308' : score >= 60 ? '#f97316' : '#ef4444';
      const gradeLabel = score >= 90 ? 'Excellent' : score >= 80 ? 'Good' : score >= 70 ? 'Fair' : score >= 60 ? 'Poor' : 'Critical';

      // Helper function to escape HTML
      const escapeHtml = (text: string) => {
        if (!text) return 'N/A';
        return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      };

      // Generate compliance cards HTML
      const complianceCardsHtml = report.compliance ? Object.entries(report.compliance).map(([key, data]: [string, any]) => {
        const statusColor = data.status === 'compliant' ? '#10b981' : data.status === 'partial' ? '#f59e0b' : '#ef4444';
        const statusBg = data.status === 'compliant' ? '#d1fae5' : data.status === 'partial' ? '#fef3c7' : '#fee2e2';
        const statusIcon = data.status === 'compliant' ? '✓' : data.status === 'partial' ? '◐' : '✗';
        const standard = report.standards?.[key] || { name: key, version: '' };
        return `
          <div class="compliance-card">
            <div class="compliance-header">
              <span class="compliance-name">${escapeHtml(standard.name)}</span>
              <span class="compliance-version" title="Standard Version">v${escapeHtml(standard.version)}</span>
            </div>
            <div class="compliance-score-row">
              <div class="compliance-gauge">
                <svg viewBox="0 0 36 36" class="circular-chart">
                  <path class="circle-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
                  <path class="circle" stroke="${statusColor}" stroke-dasharray="${data.score}, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
                  <text x="18" y="20.35" class="percentage">${data.score}%</text>
                </svg>
              </div>
              <div class="compliance-status" style="background: ${statusBg}; color: ${statusColor};">
                <span class="status-icon">${statusIcon}</span>
                <span style="text-transform: capitalize;">${data.status}</span>
              </div>
            </div>
            <div class="compliance-notes">${escapeHtml(data.notes)}</div>
          </div>
        `;
      }).join('') : '';

      // Generate category cards HTML
      const categoryCardsHtml = Object.entries(report.byCategory || {}).map(([cat, count]: [string, any]) => {
        const icons: Record<string, string> = {
          sast: '🔍', secrets: '🔐', dependency: '📦', container: '🐳', infrastructure: '🏗️', license: '📄'
        };
        const colors: Record<string, string> = {
          sast: '#8b5cf6', secrets: '#ef4444', dependency: '#f59e0b', container: '#06b6d4', infrastructure: '#10b981', license: '#6366f1'
        };
        return `
          <div class="category-card" style="border-left: 4px solid ${colors[cat] || '#6b7280'};">
            <div class="category-icon">${icons[cat] || '📊'}</div>
            <div class="category-count">${count}</div>
            <div class="category-name">${cat.toUpperCase()}</div>
          </div>
        `;
      }).join('');

      // Generate severity distribution bar
      const total = report.summary.total || 1;
      const severityBar = `
        <div class="severity-bar">
          ${report.summary.critical > 0 ? `<div class="severity-segment critical" style="width: ${(report.summary.critical/total*100).toFixed(1)}%;" title="Critical: ${report.summary.critical}"></div>` : ''}
          ${report.summary.high > 0 ? `<div class="severity-segment high" style="width: ${(report.summary.high/total*100).toFixed(1)}%;" title="High: ${report.summary.high}"></div>` : ''}
          ${report.summary.medium > 0 ? `<div class="severity-segment medium" style="width: ${(report.summary.medium/total*100).toFixed(1)}%;" title="Medium: ${report.summary.medium}"></div>` : ''}
          ${report.summary.low > 0 ? `<div class="severity-segment low" style="width: ${(report.summary.low/total*100).toFixed(1)}%;" title="Low: ${report.summary.low}"></div>` : ''}
          ${report.summary.info > 0 ? `<div class="severity-segment info" style="width: ${(report.summary.info/total*100).toFixed(1)}%;" title="Info: ${report.summary.info}"></div>` : ''}
        </div>
      `;

      // Generate findings tables with better formatting
      const generateFindingsTable = (findings: any[], severity: string, maxItems: number = 50) => {
        if (!findings || findings.length === 0) return '';
        const severityColors: Record<string, { bg: string, text: string, border: string }> = {
          critical: { bg: '#fef2f2', text: '#991b1b', border: '#fecaca' },
          high: { bg: '#fff7ed', text: '#9a3412', border: '#fed7aa' },
          medium: { bg: '#fffbeb', text: '#92400e', border: '#fde68a' },
          low: { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' },
          info: { bg: '#f9fafb', text: '#374151', border: '#e5e7eb' }
        };
        const colors = severityColors[severity] || severityColors.info;

        return `
          <div class="findings-section" style="border: 1px solid ${colors.border}; background: ${colors.bg};">
            <div class="findings-header" style="background: ${colors.border}; color: ${colors.text};">
              <span class="findings-title">${severity.toUpperCase()} FINDINGS</span>
              <span class="findings-count">${findings.length} issue${findings.length !== 1 ? 's' : ''}</span>
            </div>
            <div class="findings-list">
              ${findings.slice(0, maxItems).map((f: any, idx: number) => `
                <div class="finding-item">
                  <div class="finding-number">${idx + 1}</div>
                  <div class="finding-content">
                    <div class="finding-title">${escapeHtml(f.title)}</div>
                    <div class="finding-meta">
                      <span class="finding-tool">🔧 ${escapeHtml(f.tool)}</span>
                      ${f.category ? `<span class="finding-category">📁 ${escapeHtml(f.category)}</span>` : ''}
                      ${f.file ? `<span class="finding-file">📄 ${escapeHtml(f.file)}${f.line ? ':' + f.line : ''}</span>` : ''}
                    </div>
                    ${f.codeSnippet ? `<div class="code-snippet"><code>${escapeHtml(f.codeSnippet.substring(0, 200))}</code></div>` : ''}
                    ${f.remediation ? `<div class="finding-remediation"><strong>Remediation:</strong> ${escapeHtml(f.remediation)}</div>` : ''}
                    ${f.cweId ? `<span class="finding-cwe">CWE-${escapeHtml(f.cweId)}</span>` : ''}
                  </div>
                </div>
              `).join('')}
              ${findings.length > maxItems ? `
                <div class="findings-more">
                  ... and ${findings.length - maxItems} more ${severity} findings
                </div>
              ` : ''}
            </div>
          </div>
        `;
      };

      // Generate tool versions table
      const toolVersionsHtml = report.toolVersions ? `
        <div class="tools-section">
          <h3>🛠️ Security Tools Used</h3>
          <div class="tools-grid">
            ${Object.entries(report.toolVersions).map(([tool, version]: [string, any]) => `
              <div class="tool-card">
                <div class="tool-name">${escapeHtml(tool)}</div>
                <div class="tool-version">${escapeHtml(version)}</div>
              </div>
            `).join('')}
          </div>
        </div>
      ` : '';

      // Generate comprehensive tools documentation for PDF
      const toolsDocHtml = report.toolsDocumentation ? Object.entries(report.toolsDocumentation).map(([key, tool]: [string, any]) => `
        <div class="tool-doc-card">
          <div class="tool-doc-header">
            <div class="tool-doc-title">
              <span class="tool-doc-icon">${
                tool.category === 'sast' ? '🔍' :
                tool.category === 'secrets' ? '🔐' :
                tool.category === 'dependency' ? '📦' :
                tool.category === 'container' ? '🐳' :
                tool.category === 'infrastructure' ? '🏗️' :
                tool.category === 'license' ? '📄' : '🔧'
              }</span>
              <span>${escapeHtml(tool.name)}</span>
            </div>
            <a href="${escapeHtml(tool.url)}" class="tool-doc-link">${escapeHtml(tool.url)}</a>
          </div>
          <div class="tool-doc-vendor">Vendor: ${escapeHtml(tool.vendor)}</div>
          <p class="tool-doc-desc">${escapeHtml(tool.detailedDescription)}</p>

          <div class="tool-doc-section">
            <h5>What It Checks:</h5>
            <ul class="tool-doc-checks">
              ${(tool.whatItChecks || []).map((check: string) => `<li>${escapeHtml(check)}</li>`).join('')}
            </ul>
          </div>

          <div class="tool-doc-section">
            <h5>How It Works:</h5>
            <p>${escapeHtml(tool.howItWorks)}</p>
          </div>

          <div class="tool-doc-standards">
            <span class="standards-label">Compliance Standards:</span>
            ${(tool.standards || []).map((std: string) => `<span class="standard-tag">${escapeHtml(std)}</span>`).join('')}
          </div>
        </div>
      `).join('') : '';

      // Generate methodology section for PDF
      const methodologyHtml = report.methodology ? `
        <div class="methodology-section">
          <h3>📋 Scan Methodology Overview</h3>
          <p class="methodology-overview">${escapeHtml(report.methodology.overview)}</p>
          <p style="margin: 16px 0; padding: 12px; background: #eff6ff; border-left: 4px solid #3b82f6; border-radius: 4px; font-size: 12px; color: #1e40af;">
            <strong>📖 For more details</strong> on the specific security tools and scans used in this assessment, please refer to the <strong>Security Tools Documentation</strong> section at the end of this report.
          </p>

          <div class="methodology-steps">
            ${(report.methodology.process || []).map((step: any) => `
              <div class="methodology-step">
                <div class="step-number">${step.step}</div>
                <div class="step-content">
                  <div class="step-title">${escapeHtml(step.title)}</div>
                  <p class="step-desc">${escapeHtml(step.description)}</p>
                  <div class="step-meta">
                    <span><strong>Tools:</strong> ${(step.tools || []).join(', ')}</span>
                    <span><strong>Coverage:</strong> ${escapeHtml(step.coverage)}</span>
                  </div>
                </div>
              </div>
            `).join('')}
          </div>

          <div class="scoring-section">
            <h4>Security Score Calculation</h4>
            <div class="scoring-formula">${escapeHtml(report.methodology.scoring?.formula || '')}</div>
            <p>${escapeHtml(report.methodology.scoring?.description || '')}</p>

            <div class="grading-table">
              <table>
                <thead>
                  <tr>
                    <th>Grade</th>
                    <th>Score Range</th>
                    <th>Status</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  ${(report.methodology.scoring?.grading || []).map((g: any) => `
                    <tr>
                      <td><span class="grade-badge grade-${g.grade.toLowerCase()}">${escapeHtml(g.grade)}</span></td>
                      <td>${escapeHtml(g.range)}</td>
                      <td>${escapeHtml(g.status)}</td>
                      <td>${escapeHtml(g.description)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          </div>

          <div class="important-notes">
            <h4>Important Information</h4>
            <div class="notes-grid">
              <div class="note-item">
                <strong>Data Retention:</strong>
                <p>${escapeHtml(report.methodology.dataRetention || '')}</p>
              </div>
              <div class="note-item">
                <strong>Recommended Frequency:</strong>
                <p>${escapeHtml(report.methodology.frequency || '')}</p>
              </div>
            </div>
          </div>
        </div>
      ` : '';

      // Generate detailed compliance checklist with line-by-line controls
      const generateComplianceChecklist = () => {
        if (!report.compliance || !report.standards) return '';

        const checklistHtml = Object.entries(report.compliance).map(([key, complianceItem]: [string, any]) => {
          const standard = report.standards[key];
          if (!standard || !complianceItem.details) return '';

          const statusIcon = (status: string) => {
            switch (status?.toLowerCase()) {
              case 'pass':
              case 'compliant':
                return '<span style="color: #10b981;">✓</span>';
              case 'fail':
              case 'non-compliant':
                return '<span style="color: #ef4444;">✗</span>';
              case 'partial':
                return '<span style="color: #f59e0b;">◐</span>';
              default:
                return '<span style="color: #6b7280;">○</span>';
            }
          };

          return `
            <div style="margin-bottom: 30px; page-break-inside: avoid;">
              <div style="background: linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%); padding: 16px; border-radius: 8px; border-left: 4px solid #3b82f6; margin-bottom: 16px;">
                <h4 style="font-size: 16px; font-weight: 700; color: #1e3a5f; margin-bottom: 4px;">
                  ${escapeHtml(standard.name)} ${escapeHtml(standard.version)}
                </h4>
                <p style="font-size: 12px; color: #6b7280; margin: 0;">${escapeHtml(standard.description)}</p>
              </div>

              <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
                <thead>
                  <tr style="background: #f3f4f6;">
                    <th style="padding: 8px; text-align: left; border: 1px solid #e5e7eb; width: 15%;">Control ID</th>
                    <th style="padding: 8px; text-align: left; border: 1px solid #e5e7eb; width: 50%;">Control Description</th>
                    <th style="padding: 8px; text-align: center; border: 1px solid #e5e7eb; width: 10%;">Status</th>
                    <th style="padding: 8px; text-align: center; border: 1px solid #e5e7eb; width: 15%;">Findings</th>
                    <th style="padding: 8px; text-align: left; border: 1px solid #e5e7eb; width: 10%;">Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  ${complianceItem.details.map((control: any, idx: number) => `
                    <tr style="background: ${idx % 2 === 0 ? '#ffffff' : '#f9fafb'};">
                      <td style="padding: 8px; border: 1px solid #e5e7eb; font-weight: 600; color: #1e40af;">
                        ${escapeHtml(control.code || control.id || `${key}-${idx + 1}`)}
                      </td>
                      <td style="padding: 8px; border: 1px solid #e5e7eb;">
                        <div style="font-weight: 600; margin-bottom: 2px;">${escapeHtml(control.name || control.description || 'Control Check')}</div>
                        ${control.description && control.name ? `<div style="font-size: 10px; color: #6b7280;">${escapeHtml(control.description)}</div>` : ''}
                      </td>
                      <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: center; font-size: 18px;">
                        ${statusIcon(control.status || control.result)}
                      </td>
                      <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: center;">
                        ${control.count !== undefined ? `<span style="color: ${control.count > 0 ? '#ef4444' : '#10b981'}; font-weight: 600;">${control.count}</span>` : '—'}
                      </td>
                      <td style="padding: 8px; border: 1px solid #e5e7eb; font-size: 10px; line-height: 1.4;">
                        ${control.evidence ? escapeHtml(control.evidence) : (control.status === 'pass' || control.result === 'pass' ? '<span style="color: #10b981;">✓ Verified</span>' : control.count === 0 ? '<span style="color: #10b981;">✓ No Issues</span>' : '<span style="color: #ef4444;">⚠ Review Required</span>')}
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          `;
        }).join('');

        return checklistHtml ? `
          <div class="content-page page-break">
            <div class="page-header">
              <div class="page-title">Compliance Checklist</div>
              <div class="page-scan-id">Line-by-Line Control Assessment</div>
            </div>

            <div class="section">
              <p style="margin-bottom: 20px; color: #4b5563; font-size: 12px;">
                This section provides a detailed line-by-line assessment of each security control requirement for applicable standards.
                Each control is evaluated for compliance, with findings count and evidence status.
              </p>
              ${checklistHtml}
            </div>
          </div>
        ` : '';
      };

      // Generate findings-to-standards mapping section
      const generateFindingsMapping = () => {
        if (!report.allFindings || report.allFindings.length === 0) return '';

        // Group findings by standard
        const standardsMap: Record<string, any[]> = {};

        report.allFindings.forEach((finding: any) => {
          if (finding.standards_impacted && finding.standards_impacted.length > 0) {
            finding.standards_impacted.forEach((impact: any) => {
              const key = `${impact.standard}_${impact.code}`;
              if (!standardsMap[key]) {
                standardsMap[key] = [];
              }
              standardsMap[key].push(finding);
            });
          }
        });

        if (Object.keys(standardsMap).length === 0) return '';

        const mappingHtml = Object.entries(standardsMap)
          .sort((a, b) => b[1].length - a[1].length)
          .map(([key, findings]) => {
            const impact = findings[0].standards_impacted?.find((i: any) => `${i.standard}_${i.code}` === key);
            if (!impact) return '';

            const severityCounts = {
              critical: findings.filter((f: any) => f.severity === 'critical').length,
              high: findings.filter((f: any) => f.severity === 'high').length,
              medium: findings.filter((f: any) => f.severity === 'medium').length,
              low: findings.filter((f: any) => f.severity === 'low').length,
              info: findings.filter((f: any) => f.severity === 'info').length
            };

            const colorMap: Record<string, string> = {
              OWASP: '#ef4444',
              CWE: '#f97316',
              PCI_DSS: '#3b82f6',
              ISO_27001: '#10b981',
              GDPR: '#8b5cf6',
              SOC2: '#6366f1',
              NIST: '#06b6d4'
            };

            return `
              <div style="margin-bottom: 24px; page-break-inside: avoid;">
                <div style="background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%); border-left: 4px solid ${colorMap[impact.standard] || '#6b7280'}; border-radius: 8px; padding: 14px; margin-bottom: 12px;">
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <div>
                      <span style="font-weight: 700; color: ${colorMap[impact.standard] || '#1e3a5f'}; font-size: 14px;">
                        ${escapeHtml(impact.standard)} ${escapeHtml(impact.code)}
                      </span>
                      <span style="color: #6b7280; font-size: 13px; margin-left: 8px;">
                        — ${escapeHtml(impact.name)}
                      </span>
                    </div>
                    <div style="background: #fee2e2; color: #991b1b; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600;">
                      ${findings.length} finding${findings.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div style="display: flex; gap: 12px; margin-top: 8px; font-size: 11px;">
                    ${severityCounts.critical > 0 ? `<span style="color: #dc2626;">🚨 ${severityCounts.critical} Critical</span>` : ''}
                    ${severityCounts.high > 0 ? `<span style="color: #ea580c;">⚠️ ${severityCounts.high} High</span>` : ''}
                    ${severityCounts.medium > 0 ? `<span style="color: #d97706;">📋 ${severityCounts.medium} Medium</span>` : ''}
                    ${severityCounts.low > 0 ? `<span style="color: #2563eb;">ℹ️ ${severityCounts.low} Low</span>` : ''}
                    ${severityCounts.info > 0 ? `<span style="color: #6b7280;">💡 ${severityCounts.info} Info</span>` : ''}
                  </div>
                </div>

                <div style="margin-left: 16px;">
                  ${findings.slice(0, 10).map((f: any, idx: number) => `
                    <div style="padding: 8px 0; border-bottom: 1px solid #f3f4f6; font-size: 11px;">
                      <div style="display: flex; gap: 8px; align-items: start;">
                        <span style="font-weight: 600; color: #6b7280; min-width: 24px;">${idx + 1}.</span>
                        <div style="flex: 1;">
                          <div style="font-weight: 600; color: #1e3a5f; margin-bottom: 2px;">${escapeHtml(f.title)}</div>
                          <div style="display: flex; gap: 12px; font-size: 10px; color: #6b7280;">
                            <span style="padding: 2px 6px; background: ${f.severity === 'critical' ? '#fee2e2' : f.severity === 'high' ? '#ffedd5' : f.severity === 'medium' ? '#fef3c7' : '#eff6ff'}; color: ${f.severity === 'critical' ? '#991b1b' : f.severity === 'high' ? '#9a3412' : f.severity === 'medium' ? '#92400e' : '#1e40af'}; border-radius: 4px; font-weight: 600;">
                              ${escapeHtml(f.severity?.toUpperCase() || 'UNKNOWN')}
                            </span>
                            ${f.tool ? `<span>🔧 ${escapeHtml(f.tool)}</span>` : ''}
                            ${f.file ? `<span>📄 ${escapeHtml(f.file)}${f.line ? ':' + f.line : ''}</span>` : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  `).join('')}
                  ${findings.length > 10 ? `
                    <div style="padding: 8px 0; font-size: 11px; color: #6b7280; font-style: italic;">
                      ... and ${findings.length - 10} more finding${findings.length - 10 !== 1 ? 's' : ''}
                    </div>
                  ` : ''}
                </div>
              </div>
            `;
          }).join('');

        return `
          <div class="content-page page-break">
            <div class="page-header">
              <div class="page-title">Findings-to-Standards Mapping</div>
              <div class="page-scan-id">Security Standard Impact Analysis</div>
            </div>

            <div class="section">
              <p style="margin-bottom: 20px; color: #4b5563; font-size: 12px;">
                This section maps each security finding to the relevant compliance standards it impacts.
                Use this to understand which findings affect specific regulatory requirements.
              </p>
              ${mappingHtml}
            </div>
          </div>
        `;
      };

      // Generate consolidated remediation roadmap
      const generateRemediationRoadmap = () => {
        if (!report.allFindings || report.allFindings.length === 0) return '';

        const criticalFindings = report.allFindings.filter((f: any) => f.severity === 'critical');
        const highFindings = report.allFindings.filter((f: any) => f.severity === 'high');
        const mediumFindings = report.allFindings.filter((f: any) => f.severity === 'medium');
        const lowFindings = report.allFindings.filter((f: any) => f.severity === 'low');

        const generateRoadmapSection = (title: string, findings: any[], priority: string, timeline: string, color: string) => {
          if (findings.length === 0) return '';

          // Group by category
          const byCategory: Record<string, any[]> = {};
          findings.forEach((f: any) => {
            const cat = f.category || 'other';
            if (!byCategory[cat]) byCategory[cat] = [];
            byCategory[cat].push(f);
          });

          return `
            <div style="margin-bottom: 30px; page-break-inside: avoid;">
              <div style="background: linear-gradient(135deg, ${color}15 0%, ${color}05 100%); border: 2px solid ${color}; border-radius: 12px; padding: 16px; margin-bottom: 16px;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <div>
                    <h4 style="font-size: 16px; font-weight: 700; color: ${color}; margin: 0 0 4px 0;">
                      ${title}
                    </h4>
                    <p style="font-size: 12px; color: #6b7280; margin: 0;">
                      ${findings.length} issue${findings.length !== 1 ? 's' : ''} • ${priority} Priority • ${timeline}
                    </p>
                  </div>
                  <div style="background: ${color}; color: white; padding: 8px 16px; border-radius: 8px; font-size: 24px; font-weight: 700;">
                    ${findings.length}
                  </div>
                </div>
              </div>

              ${Object.entries(byCategory).map(([category, categoryFindings]) => `
                <div style="margin: 16px 0; padding: 12px; background: #f9fafb; border-radius: 8px;">
                  <div style="font-weight: 700; font-size: 13px; color: #1e3a5f; margin-bottom: 8px; text-transform: uppercase;">
                    ${category === 'sast' ? '🔍 Static Analysis' : category === 'secrets' ? '🔐 Secrets Detection' : category === 'dependency' ? '📦 Dependencies' : category === 'container' ? '🐳 Container Security' : category === 'infrastructure' ? '🏗️ Infrastructure' : '📄 Other'}
                    <span style="color: #6b7280; font-weight: 400; text-transform: none; margin-left: 8px;">(${categoryFindings.length} finding${categoryFindings.length !== 1 ? 's' : ''})</span>
                  </div>

                  <div style="display: grid; gap: 8px;">
                    ${categoryFindings.map((finding: any, idx: number) => `
                      <div style="display: flex; gap: 8px; padding: 8px; background: white; border-radius: 6px; border-left: 3px solid ${color}; font-size: 11px;">
                        <div style="min-width: 24px; font-weight: 600; color: #6b7280;">${idx + 1}</div>
                        <div style="flex: 1;">
                          <div style="font-weight: 600; color: #1e3a5f; margin-bottom: 2px;">${escapeHtml(finding.title)}</div>
                          ${finding.file ? `<div style="color: #6b7280; font-size: 10px;">📄 ${escapeHtml(finding.file)}${finding.line ? ':' + finding.line : ''}</div>` : ''}
                          ${finding.remediation ? `<div style="color: #10b981; margin-top: 4px; padding: 4px 8px; background: #f0fdf4; border-radius: 4px;"><strong>Fix:</strong> ${escapeHtml(finding.remediation.substring(0, 150))}${finding.remediation.length > 150 ? '...' : ''}</div>` : ''}
                        </div>
                      </div>
                    `).join('')}
                  </div>
                </div>
              `).join('')}
            </div>
          `;
        };

        return `
          <div class="content-page page-break">
            <div class="page-header">
              <div class="page-title">Remediation Roadmap</div>
              <div class="page-scan-id">Prioritized Action Plan</div>
            </div>

            <div class="section">
              <p style="margin-bottom: 24px; color: #4b5563; font-size: 12px;">
                This roadmap provides a prioritized plan for addressing security findings.
                Findings are grouped by severity and category, with recommended timelines for remediation.
              </p>

              ${generateRoadmapSection('Phase 1: Critical Issues', criticalFindings, 'Immediate', 'Fix within 24-48 hours', '#dc2626')}
              ${generateRoadmapSection('Phase 2: High Priority Issues', highFindings, 'High', 'Fix within 1 week', '#ea580c')}
              ${generateRoadmapSection('Phase 3: Medium Priority Issues', mediumFindings, 'Medium', 'Fix within 2-4 weeks', '#d97706')}
              ${generateRoadmapSection('Phase 4: Low Priority Issues', lowFindings, 'Low', 'Fix within 1-2 months', '#2563eb')}

              <div style="margin-top: 30px; padding: 20px; background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); border-radius: 12px; border: 1px solid #bfdbfe;">
                <h4 style="font-size: 14px; font-weight: 700; color: #1e40af; margin: 0 0 12px 0;">💡 Remediation Best Practices</h4>
                <ul style="margin: 0; padding-left: 20px; font-size: 11px; color: #1e3a8a; line-height: 1.8;">
                  <li>Address critical and high-severity findings first to reduce immediate risk</li>
                  <li>Group similar findings together for efficient batch remediation</li>
                  <li>Verify fixes with follow-up security scans after remediation</li>
                  <li>Document remediation steps and lessons learned for future reference</li>
                  <li>Implement automated testing to prevent regression of fixed issues</li>
                </ul>
              </div>
            </div>
          </div>
        `;
      };

      // Generate new PDF sections
      const complianceChecklistHtml = generateComplianceChecklist();
      const findingsMappingHtml = generateFindingsMapping();
      const remediationRoadmapHtml = generateRemediationRoadmap();

      // Generate PDF content
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Security Scan Report - ${escapeHtml(report.scanId)}</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet">
          <style>
            @page {
              margin: 1in;
              size: letter portrait;
            }
            @page:first {
              margin: 0;
            }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body {
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              color: #1f2937;
              line-height: 1.6;
              background: #ffffff;
              font-size: 12px;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            }

            /* Cover Page */
            .cover-page {
              height: 297mm;
              background: linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%);
              color: white;
              padding: 60px;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              page-break-after: always;
              page-break-inside: avoid;
            }
            .cover-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
            }
            .cover-logo {
              font-size: 28px;
              font-weight: 700;
              display: flex;
              align-items: center;
              gap: 12px;
            }
            .cover-logo-icon {
              width: 50px;
              height: 50px;
              background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
              border-radius: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 24px;
            }
            .cover-date {
              text-align: right;
              font-size: 14px;
              opacity: 0.8;
            }
            .cover-main {
              text-align: center;
              padding: 60px 0;
            }
            .cover-title {
              font-size: 42px;
              font-weight: 700;
              margin-bottom: 16px;
              letter-spacing: -1px;
            }
            .cover-subtitle {
              font-size: 20px;
              opacity: 0.8;
              margin-bottom: 50px;
            }
            .cover-score-container {
              display: flex;
              justify-content: center;
              gap: 60px;
              margin: 40px 0;
            }
            .cover-score-box {
              text-align: center;
            }
            .cover-score-circle {
              width: 180px;
              height: 180px;
              border-radius: 50%;
              background: conic-gradient(${gradeColor} ${score * 3.6}deg, rgba(255,255,255,0.1) 0deg);
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 auto 20px;
              position: relative;
            }
            .cover-score-inner {
              width: 150px;
              height: 150px;
              border-radius: 50%;
              background: #1e3a5f;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
            }
            .cover-score-value {
              font-size: 48px;
              font-weight: 700;
            }
            .cover-score-label {
              font-size: 14px;
              opacity: 0.7;
            }
            .cover-grade-box {
              width: 100px;
              height: 100px;
              background: ${gradeColor};
              border-radius: 16px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              margin: 0 auto 20px;
            }
            .cover-grade {
              font-size: 52px;
              font-weight: 700;
            }
            .cover-grade-label {
              font-size: 14px;
              opacity: 0.9;
            }
            .cover-stats {
              display: flex;
              justify-content: center;
              gap: 40px;
              margin-top: 40px;
            }
            .cover-stat {
              text-align: center;
              padding: 20px 30px;
              background: rgba(255,255,255,0.1);
              border-radius: 12px;
            }
            .cover-stat-value {
              font-size: 32px;
              font-weight: 700;
            }
            .cover-stat-label {
              font-size: 13px;
              opacity: 0.7;
            }
            .cover-stat.critical .cover-stat-value { color: #f87171; }
            .cover-stat.high .cover-stat-value { color: #fb923c; }
            .cover-footer {
              text-align: center;
              font-size: 13px;
              opacity: 0.6;
            }

            /* Content Pages */
            .content-page {
              padding: 30px 0;
              background: white;
              min-height: auto;
              page-break-inside: avoid;
            }
            .page-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding-bottom: 16px;
              border-bottom: 2px solid #e5e7eb;
              margin-bottom: 24px;
              page-break-after: avoid;
            }
            .page-title {
              font-size: 20px;
              font-weight: 700;
              color: #1e3a5f;
              letter-spacing: -0.3px;
            }
            .page-scan-id {
              font-size: 12px;
              color: #6b7280;
            }

            /* Section Styles */
            .section {
              margin-bottom: 30px;
              page-break-inside: avoid;
              orphans: 3;
              widows: 3;
            }
            .section-title {
              font-size: 15px;
              font-weight: 700;
              color: #1e3a5f;
              margin-bottom: 16px;
              padding-bottom: 8px;
              border-bottom: 2px solid #3b82f6;
              display: flex;
              align-items: center;
              gap: 8px;
              page-break-after: avoid;
            }

            /* Executive Summary Cards */
            .exec-summary {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 16px;
              margin-bottom: 25px;
              page-break-inside: avoid;
            }
            .exec-card {
              background: #f8fafc;
              border-radius: 10px;
              padding: 16px;
              text-align: center;
              border: 1px solid #e5e7eb;
              page-break-inside: avoid;
            }
            .exec-card-icon {
              font-size: 28px;
              margin-bottom: 10px;
            }
            .exec-card-value {
              font-size: 32px;
              font-weight: 700;
              color: #1e3a5f;
            }
            .exec-card-label {
              font-size: 12px;
              color: #6b7280;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .exec-card.critical .exec-card-value { color: #dc2626; }
            .exec-card.high .exec-card-value { color: #ea580c; }
            .exec-card.medium .exec-card-value { color: #d97706; }
            .exec-card.low .exec-card-value { color: #2563eb; }

            /* Severity Distribution */
            .severity-bar {
              height: 24px;
              border-radius: 12px;
              overflow: hidden;
              display: flex;
              background: #e5e7eb;
              margin: 20px 0;
            }
            .severity-segment {
              height: 100%;
              transition: width 0.3s;
            }
            .severity-segment.critical { background: linear-gradient(135deg, #ef4444, #dc2626); }
            .severity-segment.high { background: linear-gradient(135deg, #f97316, #ea580c); }
            .severity-segment.medium { background: linear-gradient(135deg, #eab308, #ca8a04); }
            .severity-segment.low { background: linear-gradient(135deg, #3b82f6, #2563eb); }
            .severity-segment.info { background: linear-gradient(135deg, #6b7280, #4b5563); }

            .severity-legend {
              display: flex;
              justify-content: center;
              gap: 30px;
              margin-top: 15px;
            }
            .severity-legend-item {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 13px;
            }
            .severity-dot {
              width: 12px;
              height: 12px;
              border-radius: 50%;
            }
            .severity-dot.critical { background: #ef4444; }
            .severity-dot.high { background: #f97316; }
            .severity-dot.medium { background: #eab308; }
            .severity-dot.low { background: #3b82f6; }
            .severity-dot.info { background: #6b7280; }

            /* Category Cards */
            .category-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 14px;
              page-break-inside: avoid;
            }
            .category-card {
              background: white;
              border-radius: 8px;
              padding: 16px;
              display: flex;
              flex-direction: column;
              align-items: center;
              box-shadow: 0 1px 4px rgba(0,0,0,0.06);
              border: 1px solid #e5e7eb;
              page-break-inside: avoid;
            }
            .category-icon { font-size: 32px; margin-bottom: 8px; }
            .category-count { font-size: 28px; font-weight: 700; color: #1e3a5f; }
            .category-name { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }

            /* Compliance Cards */
            .compliance-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 16px;
              page-break-inside: avoid;
            }
            .compliance-card {
              background: white;
              border-radius: 10px;
              padding: 16px;
              box-shadow: 0 1px 4px rgba(0,0,0,0.06);
              border: 1px solid #e5e7eb;
              page-break-inside: avoid;
            }
            .compliance-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
            }
            .compliance-name { font-weight: 700; font-size: 14px; color: #1e3a5f; }
            .compliance-version { font-size: 12px; color: #6b7280; background: #f3f4f6; padding: 2px 8px; border-radius: 4px; }
            .compliance-score-row { display: flex; align-items: center; gap: 20px; margin-bottom: 12px; }
            .compliance-gauge { width: 60px; height: 60px; }
            .circular-chart { display: block; max-width: 100%; }
            .circle-bg { fill: none; stroke: #e5e7eb; stroke-width: 3; }
            .circle { fill: none; stroke-width: 3; stroke-linecap: round; transform: rotate(-90deg); transform-origin: 50% 50%; }
            .percentage { fill: #1e3a5f; font-size: 8px; font-weight: 700; text-anchor: middle; }
            .compliance-status {
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 600;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            .status-icon { font-size: 14px; }
            .compliance-notes { font-size: 12px; color: #6b7280; line-height: 1.4; }

            /* Findings Sections */
            .findings-section {
              border-radius: 10px;
              overflow: hidden;
              margin-bottom: 20px;
              page-break-inside: auto;
            }
            .findings-header {
              padding: 14px 20px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-weight: 700;
            }
            .findings-title { font-size: 14px; }
            .findings-count { font-size: 13px; opacity: 0.8; }
            .findings-list { padding: 10px; }
            .finding-item {
              display: flex;
              gap: 12px;
              padding: 12px;
              background: white;
              border-radius: 6px;
              margin-bottom: 8px;
              border: 1px solid rgba(0,0,0,0.05);
              page-break-inside: avoid;
            }
            .finding-number {
              width: 28px;
              height: 28px;
              background: #f3f4f6;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              font-weight: 700;
              color: #6b7280;
              flex-shrink: 0;
            }
            .finding-content { flex: 1; min-width: 0; }
            .finding-title { font-weight: 600; font-size: 13px; color: #1e3a5f; margin-bottom: 6px; }
            .finding-meta {
              display: flex;
              flex-wrap: wrap;
              gap: 12px;
              font-size: 11px;
              color: #6b7280;
              margin-bottom: 8px;
            }
            .finding-file {
              max-width: 300px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .code-snippet {
              background: #1e293b;
              color: #e2e8f0;
              padding: 8px 12px;
              border-radius: 4px;
              font-family: 'JetBrains Mono', 'Consolas', 'Monaco', monospace;
              font-size: 10px;
              margin: 6px 0;
              overflow-x: auto;
              white-space: pre-wrap;
              word-break: break-all;
            }
            .finding-remediation {
              font-size: 12px;
              color: #4b5563;
              background: #f0fdf4;
              padding: 10px 12px;
              border-radius: 6px;
              border-left: 3px solid #22c55e;
              margin-top: 8px;
            }
            .finding-cwe {
              display: inline-block;
              background: #ede9fe;
              color: #7c3aed;
              padding: 2px 8px;
              border-radius: 4px;
              font-size: 10px;
              font-weight: 600;
              margin-top: 8px;
            }
            .findings-more {
              text-align: center;
              padding: 15px;
              color: #6b7280;
              font-size: 13px;
              font-style: italic;
            }

            /* Tools Section */
            .tools-section {
              margin-top: 24px;
              page-break-inside: avoid;
            }
            .tools-section h3 { font-size: 14px; margin-bottom: 12px; color: #1e3a5f; }
            .tools-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 10px;
              page-break-inside: avoid;
            }
            .tool-card {
              background: #f8fafc;
              padding: 10px 12px;
              border-radius: 6px;
              border: 1px solid #e5e7eb;
              page-break-inside: avoid;
            }
            .tool-name { font-size: 11px; font-weight: 600; color: #1e3a5f; }
            .tool-version { font-size: 10px; color: #6b7280; }

            /* Footer */
            .report-footer {
              margin-top: 30px;
              padding-top: 16px;
              border-top: 2px solid #e5e7eb;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 11px;
              color: #6b7280;
              page-break-inside: avoid;
            }
            .footer-logo {
              display: flex;
              align-items: center;
              gap: 6px;
              font-weight: 600;
              color: #1e3a5f;
            }

            /* Page Breaks */
            .page-break {
              page-break-before: always;
              break-before: page;
              padding-top: 0;
            }

            /* Tools Documentation Styles */
            .tool-doc-card {
              background: white;
              border: 1px solid #e5e7eb;
              border-radius: 10px;
              padding: 18px;
              margin-bottom: 16px;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .tool-doc-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 10px;
              padding-bottom: 10px;
              border-bottom: 1px solid #e5e7eb;
            }
            .tool-doc-title {
              font-size: 18px;
              font-weight: 700;
              color: #1e3a5f;
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .tool-doc-icon { font-size: 24px; }
            .tool-doc-link {
              font-size: 12px;
              color: #3b82f6;
              text-decoration: none;
            }
            .tool-doc-vendor {
              font-size: 12px;
              color: #6b7280;
              margin-bottom: 10px;
            }
            .tool-doc-desc {
              font-size: 13px;
              color: #4b5563;
              line-height: 1.6;
              margin-bottom: 15px;
            }
            .tool-doc-section {
              margin-bottom: 15px;
            }
            .tool-doc-section h5 {
              font-size: 13px;
              font-weight: 600;
              color: #1e3a5f;
              margin-bottom: 8px;
            }
            .tool-doc-section p {
              font-size: 12px;
              color: #4b5563;
              line-height: 1.5;
            }
            .tool-doc-checks {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 4px;
              font-size: 11px;
              color: #4b5563;
              list-style: none;
              padding: 0;
            }
            .tool-doc-checks li {
              padding-left: 15px;
              position: relative;
            }
            .tool-doc-checks li:before {
              content: "•";
              color: #3b82f6;
              position: absolute;
              left: 0;
            }
            .tool-doc-standards {
              margin-top: 15px;
              padding-top: 15px;
              border-top: 1px solid #e5e7eb;
            }
            .standards-label {
              font-size: 12px;
              font-weight: 600;
              color: #1e3a5f;
              margin-right: 10px;
            }
            .standard-tag {
              display: inline-block;
              padding: 3px 8px;
              background: #ede9fe;
              color: #7c3aed;
              font-size: 10px;
              border-radius: 4px;
              margin: 2px;
            }

            /* Methodology Section Styles */
            .methodology-section {
              background: #f8fafc;
              border-radius: 10px;
              padding: 20px;
              margin-bottom: 25px;
              page-break-inside: avoid;
            }
            .methodology-section h3 {
              font-size: 16px;
              font-weight: 700;
              color: #1e3a5f;
              margin-bottom: 12px;
            }
            .methodology-overview {
              font-size: 12px;
              color: #4b5563;
              line-height: 1.6;
              margin-bottom: 20px;
            }
            .methodology-steps {
              display: grid;
              gap: 12px;
              margin-bottom: 20px;
            }
            .methodology-step {
              display: flex;
              gap: 12px;
              background: white;
              border-radius: 8px;
              padding: 12px;
              border: 1px solid #e5e7eb;
              page-break-inside: avoid;
            }
            .step-number {
              width: 36px;
              height: 36px;
              background: linear-gradient(135deg, #3b82f6, #8b5cf6);
              color: white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: 700;
              font-size: 16px;
              flex-shrink: 0;
            }
            .step-content { flex: 1; }
            .step-title {
              font-size: 14px;
              font-weight: 700;
              color: #1e3a5f;
              margin-bottom: 5px;
            }
            .step-desc {
              font-size: 12px;
              color: #4b5563;
              margin-bottom: 8px;
            }
            .step-meta {
              display: flex;
              gap: 20px;
              font-size: 11px;
              color: #6b7280;
            }

            /* Scoring Section */
            .scoring-section {
              background: white;
              border-radius: 8px;
              padding: 16px;
              margin-bottom: 16px;
              border: 1px solid #e5e7eb;
              page-break-inside: avoid;
            }
            .scoring-section h4 {
              font-size: 16px;
              font-weight: 700;
              color: #1e3a5f;
              margin-bottom: 10px;
            }
            .scoring-formula {
              background: #1e293b;
              color: #a5b4fc;
              padding: 10px 14px;
              border-radius: 6px;
              font-family: 'JetBrains Mono', 'Consolas', monospace;
              font-size: 12px;
              margin-bottom: 12px;
            }
            .grading-table {
              margin-top: 16px;
              page-break-inside: avoid;
            }
            .grading-table table {
              width: 100%;
              border-collapse: collapse;
            }
            .grading-table th, .grading-table td {
              padding: 8px 10px;
              text-align: left;
              border-bottom: 1px solid #e5e7eb;
              font-size: 11px;
            }
            .grading-table th {
              background: #f3f4f6;
              font-weight: 600;
              color: #1e3a5f;
            }
            .grading-table tr {
              page-break-inside: avoid;
            }
            .grade-badge {
              display: inline-block;
              width: 28px;
              height: 28px;
              line-height: 28px;
              text-align: center;
              border-radius: 6px;
              font-weight: 700;
              color: white;
            }
            .grade-badge.grade-a { background: #10b981; }
            .grade-badge.grade-b { background: #22c55e; }
            .grade-badge.grade-c { background: #eab308; }
            .grade-badge.grade-d { background: #f97316; }
            .grade-badge.grade-f { background: #ef4444; }

            /* Important Notes */
            .important-notes {
              background: #eff6ff;
              border: 1px solid #bfdbfe;
              border-radius: 8px;
              padding: 16px;
              page-break-inside: avoid;
            }
            .important-notes h4 {
              font-size: 14px;
              font-weight: 700;
              color: #1e40af;
              margin-bottom: 15px;
            }
            .notes-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 15px;
            }
            .note-item strong {
              display: block;
              font-size: 12px;
              color: #1e40af;
              margin-bottom: 5px;
            }
            .note-item p {
              font-size: 11px;
              color: #1e3a8a;
              line-height: 1.5;
            }

            /* Print Styles */
            @media print {
              body {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              .cover-page {
                height: auto;
                min-height: 100vh;
              }
              .page-break {
                page-break-before: always;
                break-before: page;
              }
              .no-print { display: none !important; }
              .section,
              .exec-card,
              .category-card,
              .compliance-card,
              .finding-item,
              .tool-doc-card,
              .methodology-step,
              .scoring-section,
              .important-notes {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              .exec-summary,
              .category-grid,
              .compliance-grid {
                page-break-inside: avoid !important;
              }
              h1, h2, h3, h4, h5, h6 {
                page-break-after: avoid;
                break-after: avoid;
              }
              p, li {
                orphans: 3;
                widows: 3;
              }
            }
          </style>
        </head>
        <body>
          <!-- Cover Page -->
          <div class="cover-page">
            <div class="cover-header">
              <div class="cover-logo">
                <span>Security Scanner</span>
              </div>
              <div class="cover-date">
                <div>Report Generated</div>
                <div style="font-size: 16px; font-weight: 600;">${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
              </div>
            </div>

            <div class="cover-main">
              <div class="cover-title">Security Scan Report</div>
              <div class="cover-subtitle">Comprehensive Security Assessment Analysis</div>

              <div class="cover-score-container">
                <div class="cover-score-box">
                  <div class="cover-score-circle">
                    <div class="cover-score-inner">
                      <div class="cover-score-value">${score.toFixed(1)}</div>
                      <div class="cover-score-label">out of 100</div>
                    </div>
                  </div>
                  <div style="font-size: 14px; opacity: 0.7;">Security Score</div>
                </div>
                <div class="cover-score-box">
                  <div class="cover-grade-box">
                    <div class="cover-grade">${grade}</div>
                  </div>
                  <div style="font-size: 18px; font-weight: 600;">${gradeLabel}</div>
                  <div style="font-size: 14px; opacity: 0.7;">Overall Grade</div>
                </div>
              </div>

              <div class="cover-stats">
                <div class="cover-stat">
                  <div class="cover-stat-value">${report.summary.total}</div>
                  <div class="cover-stat-label">Total Findings</div>
                </div>
                <div class="cover-stat critical">
                  <div class="cover-stat-value">${report.summary.critical}</div>
                  <div class="cover-stat-label">Critical</div>
                </div>
                <div class="cover-stat high">
                  <div class="cover-stat-value">${report.summary.high}</div>
                  <div class="cover-stat-label">High</div>
                </div>
                <div class="cover-stat">
                  <div class="cover-stat-value">${report.filesScanned > 0 ? report.filesScanned : (Object.keys(report.byTool || {}).length > 0 ? '✓' : '—')}</div>
                  <div class="cover-stat-label">${report.filesScanned > 0 ? 'Files Scanned' : 'Scan Complete'}</div>
                </div>
              </div>
            </div>

            <div class="cover-footer">
              <div>Scan ID: ${escapeHtml(report.scanId)} | Duration: ${report.duration ? `${Math.floor(report.duration / 60)}m ${report.duration % 60}s` : 'N/A'}</div>
              <div>Completed: ${new Date(report.completedAt).toLocaleString()}</div>
            </div>
          </div>

          <!-- Executive Summary Page -->
          <div class="content-page">
            <div class="page-header">
              <div class="page-title">Executive Summary</div>
              <div class="page-scan-id">Scan ID: ${escapeHtml(report.scanId)}</div>
            </div>

            <div class="section">
              <div class="section-title">Key Metrics</div>
              <div class="exec-summary">
                <div class="exec-card critical">
                  <div class="exec-card-icon" style="color: #1e3a5f;">⬤</div>
                  <div class="exec-card-value">${report.summary.critical}</div>
                  <div class="exec-card-label">Critical</div>
                </div>
                <div class="exec-card high">
                  <div class="exec-card-icon" style="color: #1e3a5f;">▲</div>
                  <div class="exec-card-value">${report.summary.high}</div>
                  <div class="exec-card-label">High</div>
                </div>
                <div class="exec-card medium">
                  <div class="exec-card-icon" style="color: #1e3a5f;">■</div>
                  <div class="exec-card-value">${report.summary.medium}</div>
                  <div class="exec-card-label">Medium</div>
                </div>
                <div class="exec-card low">
                  <div class="exec-card-icon" style="color: #1e3a5f;">●</div>
                  <div class="exec-card-value">${report.summary.low + report.summary.info}</div>
                  <div class="exec-card-label">Low/Info</div>
                </div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">Severity Distribution</div>
              ${severityBar}
              <div class="severity-legend">
                <div class="severity-legend-item"><div class="severity-dot critical"></div>Critical (${report.summary.critical})</div>
                <div class="severity-legend-item"><div class="severity-dot high"></div>High (${report.summary.high})</div>
                <div class="severity-legend-item"><div class="severity-dot medium"></div>Medium (${report.summary.medium})</div>
                <div class="severity-legend-item"><div class="severity-dot low"></div>Low (${report.summary.low})</div>
                <div class="severity-legend-item"><div class="severity-dot info"></div>Info (${report.summary.info})</div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">Findings by Category</div>
              <div class="category-grid">
                ${categoryCardsHtml}
              </div>
            </div>

            ${(report.criticalFindings?.length > 0 || report.highFindings?.length > 0) ? `
            <div class="section">
              <div class="section-title">Priority Findings</div>
              <p style="font-size: 12px; color: #6b7280; margin-bottom: 16px;">
                The following critical and high-severity issues require immediate attention:
              </p>
              ${report.criticalFindings && report.criticalFindings.length > 0 ? `
                <div style="margin-bottom: 16px;">
                  <h4 style="font-size: 13px; font-weight: 700; color: #dc2626; margin-bottom: 10px;">
                    🚨 Critical Issues (${report.criticalFindings.length})
                  </h4>
                  <div style="display: grid; gap: 8px;">
                    ${report.criticalFindings.slice(0, 5).map((f: any, idx: number) => `
                      <div style="padding: 10px; background: #fef2f2; border-left: 3px solid #dc2626; border-radius: 4px; font-size: 11px;">
                        <div style="font-weight: 600; color: #1e3a5f; margin-bottom: 2px;">${idx + 1}. ${escapeHtml(f.title)}</div>
                        <div style="color: #6b7280;">
                          ${f.tool ? `🔧 ${escapeHtml(f.tool)}` : ''}
                          ${f.file ? `📄 ${escapeHtml(f.file)}${f.line ? ':' + f.line : ''}` : ''}
                        </div>
                      </div>
                    `).join('')}
                    ${report.criticalFindings.length > 5 ? `
                      <div style="font-size: 11px; color: #6b7280; font-style: italic; padding: 6px;">
                        ... and ${report.criticalFindings.length - 5} more critical finding${report.criticalFindings.length - 5 !== 1 ? 's' : ''} (see Priority Findings section for full list)
                      </div>
                    ` : ''}
                  </div>
                </div>
              ` : ''}
              ${report.highFindings && report.highFindings.length > 0 ? `
                <div>
                  <h4 style="font-size: 13px; font-weight: 700; color: #ea580c; margin-bottom: 10px;">
                    ⚠️ High Severity Issues (${report.highFindings.length})
                  </h4>
                  <div style="display: grid; gap: 8px;">
                    ${report.highFindings.slice(0, 5).map((f: any, idx: number) => `
                      <div style="padding: 10px; background: #fff7ed; border-left: 3px solid #ea580c; border-radius: 4px; font-size: 11px;">
                        <div style="font-weight: 600; color: #1e3a5f; margin-bottom: 2px;">${idx + 1}. ${escapeHtml(f.title)}</div>
                        <div style="color: #6b7280;">
                          ${f.tool ? `🔧 ${escapeHtml(f.tool)}` : ''}
                          ${f.file ? `📄 ${escapeHtml(f.file)}${f.line ? ':' + f.line : ''}` : ''}
                        </div>
                      </div>
                    `).join('')}
                    ${report.highFindings.length > 5 ? `
                      <div style="font-size: 11px; color: #6b7280; font-style: italic; padding: 6px;">
                        ... and ${report.highFindings.length - 5} more high severity finding${report.highFindings.length - 5 !== 1 ? 's' : ''} (see Priority Findings section for full list)
                      </div>
                    ` : ''}
                  </div>
                </div>
              ` : ''}
            </div>
            ` : ''}
          </div>

          <!-- Compliance Page -->
          ${report.compliance && Object.keys(report.compliance).length > 0 ? `
          <div class="content-page page-break">
            <div class="page-header">
              <div class="page-title">Compliance Status</div>
              <div class="page-scan-id">Security Standards Assessment</div>
            </div>

            <div class="section">
              <div class="section-title">Security Standards Compliance</div>
              <div class="compliance-grid">
                ${complianceCardsHtml}
              </div>
            </div>
          </div>
          ` : ''}

          <!-- Compliance Checklist -->
          ${complianceChecklistHtml}

          <!-- Critical & High Findings Page -->
          ${(report.criticalFindings?.length > 0 || report.highFindings?.length > 0) ? `
          <div class="content-page page-break">
            <div class="page-header">
              <div class="page-title">Priority Findings</div>
              <div class="page-scan-id">Immediate Attention Required</div>
            </div>

            ${generateFindingsTable(report.criticalFindings, 'critical', 25)}
            ${generateFindingsTable(report.highFindings, 'high', 25)}
          </div>
          ` : ''}

          <!-- Medium & Low Findings Page -->
          ${(report.mediumFindings?.length > 0 || report.lowFindings?.length > 0) ? `
          <div class="content-page page-break">
            <div class="page-header">
              <div class="page-title">📋 Additional Findings</div>
              <div class="page-scan-id">Recommended for Review</div>
            </div>

            ${generateFindingsTable(report.mediumFindings, 'medium', 30)}
            ${generateFindingsTable(report.lowFindings, 'low', 20)}
          </div>
          ` : ''}

          <!-- Info Findings Page (if any) -->
          ${report.infoFindings?.length > 0 ? `
          <div class="content-page page-break">
            <div class="page-header">
              <div class="page-title">Informational Findings</div>
              <div class="page-scan-id">Best Practice Recommendations</div>
            </div>

            ${generateFindingsTable(report.infoFindings, 'info', 30)}
          </div>
          ` : ''}

          <!-- Scan Methodology Page -->
          ${report.methodology ? `
          <div class="content-page page-break">
            <div class="page-header">
              <div class="page-title">📋 Scan Methodology</div>
              <div class="page-scan-id">How We Perform Security Scans</div>
            </div>

            ${methodologyHtml}
          </div>
          ` : ''}

          <!-- Findings-to-Standards Mapping -->
          ${findingsMappingHtml}

          <!-- Tools Documentation Page -->
          ${report.toolsDocumentation && Object.keys(report.toolsDocumentation).length > 0 ? `
          <div class="content-page page-break">
            <div class="page-header">
              <div class="page-title">Security Tools Documentation</div>
              <div class="page-scan-id">Detailed Tool Information & References</div>
            </div>

            <div class="section">
              <p style="margin-bottom: 20px; color: #4b5563;">
                The following security tools are used to perform comprehensive security analysis.
                Each tool is open-source and industry-recognized. Click the links below for official documentation.
              </p>

              ${toolsDocHtml}
            </div>
          </div>
          ` : ''}

          <!-- Remediation Roadmap -->
          ${remediationRoadmapHtml}

        </body>
        </html>
      `;

      // Open print dialog for PDF
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      toast.success('PDF Report ready for download');
    } catch (error: any) {
      console.error('Failed to export PDF report:', error);
      toast.error('Failed to export PDF report');
    }
  };

  // Legacy export function for backward compatibility
  const exportReport = exportReportJSON;

  // Update result status
  const updateResultStatus = async (resultId: number, status: string) => {
    try {
      await api.put(`/api/security-scan/result/${resultId}/status`, { status });
      toast.success('Status updated');

      console.log('🔄 Refreshing data after status update...');

      // Refresh both findings table and dashboard overview
      const refreshPromises = [];

      if (selectedScanId) {
        refreshPromises.push(fetchScanResults(selectedScanId));
      }
      refreshPromises.push(fetchDashboard());

      await Promise.all(refreshPromises);
      console.log('✅ Refresh completed');
    } catch (error: any) {
      console.error('❌ Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  // Bulk update result statuses
  const bulkUpdateResultStatus = async (status: string) => {
    if (selectedResults.size === 0) {
      toast.error('No results selected');
      return;
    }

    try {
      setBulkUpdating(true);
      const resultIds = Array.from(selectedResults);

      // Update all selected results
      await Promise.all(
        resultIds.map(id => api.put(`/api/security-scan/result/${id}/status`, { status }))
      );

      toast.success(`Updated ${resultIds.length} findings to "${status}"`);
      setSelectedResults(new Set());

      console.log('🔄 Refreshing data after bulk status update...');

      // Refresh both findings table and dashboard overview
      const refreshPromises = [];

      if (selectedScanId) {
        refreshPromises.push(fetchScanResults(selectedScanId));
      }
      refreshPromises.push(fetchDashboard());

      await Promise.all(refreshPromises);
      console.log('✅ Bulk refresh completed');
    } catch (error: any) {
      console.error('❌ Failed to bulk update statuses:', error);
      toast.error('Failed to update some findings');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Toggle result selection
  const toggleResultSelection = (resultId: number) => {
    setSelectedResults(prev => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  // Select all visible results
  const selectAllResults = () => {
    if (selectedResults.size === filteredScanResults.length) {
      setSelectedResults(new Set());
    } else {
      setSelectedResults(new Set(filteredScanResults.map(r => r.id)));
    }
  };

  // Copy command to clipboard
  const copyCommand = (command: string, resultId: number) => {
    navigator.clipboard.writeText(command);
    setCopiedCommand(resultId);
    setTimeout(() => setCopiedCommand(null), 2000);
    toast.success('Command copied to clipboard');
  };

  // Initial load
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Poll for scan status when scanning
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (scanning && currentScanId) {
      interval = setInterval(() => {
        pollScanStatus(currentScanId);
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [scanning, currentScanId, pollScanStatus]);

  // Fetch results when selected scan changes (for both overview and results tabs)
  useEffect(() => {
    if (selectedScanId) {
      fetchScanResults(selectedScanId);
    }
  }, [selectedScanId, fetchScanResults]);

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Format duration
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-electric-blue" />
        <span className="ml-2 text-gray-600">Loading security dashboard...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-super-dark-purple" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Security Scan Dashboard</h1>
            <p className="text-sm text-gray-500">
              Monitor and manage security vulnerabilities across your codebase
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={scanType}
            onChange={(e) => setScanType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-electric-blue focus:border-transparent"
            disabled={scanning}
          >
            <option value="full">Full Scan</option>
            <option value="sast">SAST Only</option>
            <option value="secrets">Secrets Only</option>
            <option value="dependencies">Dependencies Only</option>
            <option value="container">Container Only</option>
            <option value="infrastructure">Infrastructure Only</option>
            <option value="licenses">Licenses Only</option>
          </select>
          <button
            onClick={startScan}
            disabled={scanning}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-colors ${
              scanning
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-super-dark-purple hover:bg-electric-blue'
            }`}
          >
            {scanning ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Run Scan
              </>
            )}
          </button>
          <button
            onClick={fetchDashboard}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'results', label: 'Findings', icon: AlertCircle },
            { id: 'history', label: 'Scan History', icon: Clock },
            { id: 'tools', label: 'Tools Status', icon: Settings }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-super-dark-purple text-super-dark-purple'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Scan Progress Bar - Show when scanning */}
      {scanning && (
        <ScanProgressBar progress={scanProgress} />
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Security Score */}
            <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center">
              <h3 className="text-sm font-medium text-gray-500 mb-4">Security Score</h3>
              <SecurityScoreGauge score={Number(dashboardData?.latestScan?.security_score || 0)} />
            </div>

            {/* Critical Issues */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-500">Critical Issues</h3>
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-4xl font-bold text-red-600">
                {dashboardData?.openCriticalCount || 0}
              </p>
              <p className="text-sm text-gray-500 mt-2">Requires immediate attention</p>
            </div>

            {/* High Issues */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-500">High Severity</h3>
                <AlertCircle className="w-5 h-5 text-orange-500" />
              </div>
              <p className="text-4xl font-bold text-orange-600">
                {dashboardData?.openHighCount || 0}
              </p>
              <p className="text-sm text-gray-500 mt-2">Should be fixed soon</p>
            </div>

            {/* Total Findings */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-500">Total Findings</h3>
                <FileSearch className="w-5 h-5 text-gray-500" />
              </div>
              <p className="text-4xl font-bold text-gray-900">
                {dashboardData?.latestScan?.total_findings || 0}
              </p>
              <div className="flex gap-2 mt-2">
                <span className="text-xs text-red-600">{dashboardData?.openIssues || 0} open</span>
                <span className="text-xs text-blue-600">{dashboardData?.inProgressIssues || 0} in progress</span>
                <span className="text-xs text-green-600">{dashboardData?.fixedIssues || 0} fixed</span>
              </div>
            </div>
          </div>

          {/* Last Scan Info */}
          {dashboardData?.latestScan && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Last Scan</h3>
                <div className="flex items-center gap-2">
                  <StatusBadge status={dashboardData.latestScan.status} />
                  <div className="relative group">
                    <button
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1"
                      title="Download Report"
                    >
                      <Download className="w-4 h-4" />
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                      <button
                        onClick={() => exportReportJSON(dashboardData.latestScan!.id)}
                        className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4" />
                        Export as JSON
                      </button>
                      <button
                        onClick={() => exportReportPDF(dashboardData.latestScan!.id)}
                        className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4" />
                        Export as PDF
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Completed:</span>
                  <p className="font-medium">{formatDate(dashboardData.latestScan.completed_at)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Duration:</span>
                  <p className="font-medium">{formatDuration(dashboardData.latestScan.scan_duration_seconds)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Scan Type:</span>
                  <p className="font-medium capitalize">{dashboardData.latestScan.scan_type}</p>
                </div>
                <div>
                  <span className="text-gray-500">Score:</span>
                  <p className="font-medium flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-blue-700 ${
                      Number(dashboardData?.latestScan?.security_score || 0) >= 90 ? 'bg-green-500' :
                      Number(dashboardData?.latestScan?.security_score || 0) >= 80 ? 'bg-green-400' :
                      Number(dashboardData?.latestScan?.security_score || 0) >= 70 ? 'bg-yellow-500' :
                      Number(dashboardData?.latestScan?.security_score || 0) >= 60 ? 'bg-yellow-600' :
                      'bg-red-500'
                    }`}>
                      {Number(dashboardData?.latestScan?.security_score || 0) >= 90 ? 'A' : Number(dashboardData?.latestScan?.security_score || 0) >= 80 ? 'B' : Number(dashboardData?.latestScan?.security_score || 0) >= 70 ? 'C' : Number(dashboardData?.latestScan?.security_score || 0) >= 60 ? 'D' : 'F'}
                    </span>
                    {Number(dashboardData?.latestScan?.security_score || 0).toFixed(1)}/100
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Findings by Category - Filter out license category */}
          {dashboardData?.breakdown?.byCategory && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Findings by Category</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {Object.entries(dashboardData.breakdown.byCategory)
                  .filter(([category]) => category.toLowerCase() !== 'license')
                  .map(([category, count]) => (
                  <div key={category} className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                    <div className="p-2 bg-white rounded-full shadow-sm mb-2">
                      {getCategoryIcon(category)}
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{count}</p>
                    <p className="text-xs text-gray-500 capitalize">{category}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Severity Distribution */}
          {dashboardData?.breakdown && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Severity Distribution</h3>
                <div className="relative group">
                  <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
                    <Info className="w-5 h-5" />
                  </button>
                  {/* Severity Classification Tooltip */}
                  <div className="absolute right-0 top-8 w-96 bg-white rounded-lg shadow-xl border border-gray-200 p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      How We Classify Severity
                    </h4>
                    <div className="space-y-3 text-xs">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-16 h-6 bg-red-500 rounded flex items-center justify-center text-blue-700 font-medium text-xs">
                          Critical
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          Immediate security risk requiring urgent action. Exploitable vulnerabilities that could lead to system compromise, data breach, or service disruption.
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-16 h-6 bg-orange-500 rounded flex items-center justify-center text-blue-700 font-medium text-xs">
                          High
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          Significant security concern that should be addressed promptly. Vulnerabilities with high impact but may require specific conditions to exploit.
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-16 h-6 bg-yellow-500 rounded flex items-center justify-center text-blue-700 font-medium text-xs">
                          Medium
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          Moderate security issue that should be remediated. May have limited impact or require multiple conditions to exploit.
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-16 h-6 bg-blue-500 rounded flex items-center justify-center text-blue-700 font-medium text-xs">
                          Low
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          Minor security concern with minimal risk. Should be addressed during regular maintenance or code review cycles.
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-16 h-6 bg-gray-400 rounded flex items-center justify-center text-blue-700 font-medium text-xs">
                          Info
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          Informational finding or best practice recommendation. No immediate security risk but may improve overall security posture.
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 italic">
                        Severity levels are determined by security scanning tools based on industry standards (CVSS, CWE) and potential business impact.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Critical', count: filteredBreakdown.critical, color: 'bg-red-500' },
                  { label: 'High', count: filteredBreakdown.high, color: 'bg-orange-500' },
                  { label: 'Medium', count: filteredBreakdown.medium, color: 'bg-yellow-500' },
                  { label: 'Low', count: filteredBreakdown.low, color: 'bg-blue-500' },
                  { label: 'Info', count: filteredBreakdown.info, color: 'bg-gray-400' }
                ].map((item) => {
                  const total = filteredBreakdown.total || 1;
                  const percentage = (item.count / total) * 100;
                  return (
                    <div key={item.label} className="flex items-center gap-4">
                      <span className="w-20 text-sm text-gray-600">{item.label}</span>
                      <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${item.color} transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="w-16 text-sm font-medium text-right">{item.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Trends */}
          {dashboardData?.trends && dashboardData.trends.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Security Score Trend</h3>
                <span className="text-sm text-gray-500">Last {dashboardData.trends.length} scans</span>
              </div>
              {/* Chart container with gradient background */}
              <div className="relative bg-gradient-to-b from-gray-50 to-white rounded-lg p-4 border border-gray-100">
                {/* Y-axis labels */}
                <div className="absolute left-0 top-4 bottom-8 w-8 flex flex-col justify-between text-xs text-gray-400">
                  <span>100</span>
                  <span>75</span>
                  <span>50</span>
                  <span>25</span>
                  <span>0</span>
                </div>
                {/* Grid lines */}
                <div className="absolute left-10 right-4 top-4 bottom-8 flex flex-col justify-between pointer-events-none">
                  {[100, 75, 50, 25, 0].map((line) => (
                    <div key={line} className="border-t border-gray-100 w-full" />
                  ))}
                </div>
                {/* Line Chart */}
                <div className="ml-10 h-48 relative">
                  <svg className="w-full h-40" viewBox="0 0 1000 100" preserveAspectRatio="none">
                    {/* Connected Line */}
                    <polyline
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      points={dashboardData.trends.map((scan, index) => {
                        const score = scan.security_score || 0;
                        const x = (index / (dashboardData.trends.length - 1)) * 1000;
                        const y = 100 - score;
                        return `${x},${y}`;
                      }).join(' ')}
                    />
                  </svg>
                  {/* Data points with labels */}
                  <div className="absolute inset-0 flex items-center">
                    {dashboardData.trends.map((scan, index) => {
                      const score = scan.security_score || 0;
                      const xPercent = (index / (dashboardData.trends.length - 1)) * 100;
                      const yPercent = score;
                      const getPointColor = (score: number) => {
                        if (score >= 80) return 'bg-green-500';
                        if (score >= 60) return 'bg-yellow-500';
                        if (score >= 40) return 'bg-orange-500';
                        return 'bg-red-500';
                      };
                      const getTextColor = (score: number) => {
                        if (score >= 80) return 'text-green-600';
                        if (score >= 60) return 'text-yellow-600';
                        if (score >= 40) return 'text-orange-600';
                        return 'text-red-600';
                      };
                      return (
                        <div
                          key={index}
                          className="absolute group"
                          style={{
                            left: `${xPercent}%`,
                            bottom: `${yPercent}%`,
                            transform: 'translate(-50%, 50%)'
                          }}
                        >
                          {/* Score label */}
                          <div className={`absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-semibold whitespace-nowrap ${getTextColor(score)}`}>
                            {Math.round(score)}
                          </div>
                          {/* Data point */}
                          <div className={`w-3 h-3 rounded-full ${getPointColor(score)} border-2 border-white shadow-md cursor-pointer transition-transform hover:scale-150`}
                            title={`Score: ${Math.round(score)}%, Date: ${formatDate(scan.completed_at)}`}
                          />
                          {/* Tooltip on hover */}
                          <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-gray-900 text-blue-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                            Score: {Math.round(score)}%<br />
                            {scan.total_findings || 0} findings
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Date labels at bottom */}
                  <div className="flex justify-between mt-2">
                    {dashboardData.trends.map((scan, index) => (
                      <span key={index} className="text-xs text-gray-500">
                        {scan.completed_at ? new Date(scan.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-gradient-to-r from-green-400 to-green-600" />
                  <span className="text-gray-600">Excellent (80+)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-gradient-to-r from-yellow-400 to-yellow-600" />
                  <span className="text-gray-600">Good (60-79)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-gradient-to-r from-orange-400 to-orange-600" />
                  <span className="text-gray-600">Fair (40-59)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-gradient-to-r from-red-400 to-red-600" />
                  <span className="text-gray-600">Poor (&lt;40)</span>
                </div>
              </div>
            </div>
          )}

          {/* Security Standards Compliance */}
          {complianceData && !complianceData.noScanData && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Security Standards Compliance</h3>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                    <span className="text-gray-600">Compliant</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                    <span className="text-gray-600">Partial</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <span className="text-gray-600">Non-Compliant</span>
                  </div>
                </div>
              </div>

              {/* Compliance Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {Object.entries(complianceData.standards).map(([key, standard]) => {
                  const compliance = complianceData.compliance?.[key];
                  const score = compliance?.score || 0;
                  const statusColor = compliance?.status === 'compliant' ? 'green' :
                                     compliance?.status === 'partial' ? 'yellow' : 'red';
                  const borderColor = statusColor === 'green' ? 'border-green-200' :
                                     statusColor === 'yellow' ? 'border-yellow-200' : 'border-red-200';
                  const bgGradient = statusColor === 'green' ? 'from-green-50 to-white' :
                                    statusColor === 'yellow' ? 'from-yellow-50 to-white' : 'from-red-50 to-white';

                  const isExpanded = expandedStandards.has(key);
                  const toggleExpand = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    setExpandedStandards(prev => {
                      const newExpanded = new Set(prev);
                      if (newExpanded.has(key)) {
                        newExpanded.delete(key);
                      } else {
                        newExpanded.add(key);
                      }
                      return newExpanded;
                    });
                  };

                  return (
                    <div
                      key={key}
                      onClick={() => filterByStandard(key)}
                      className={`relative bg-gradient-to-br ${bgGradient} rounded-xl border-2 ${borderColor} transition-all cursor-pointer hover:shadow-lg`}
                      title="Click to filter findings by this standard"
                    >
                      {/* Card Header */}
                      <div className="p-4">
                        {/* Status indicator */}
                        <div className={`absolute top-3 right-3 w-3 h-3 rounded-full ${
                          statusColor === 'green' ? 'bg-green-500' :
                          statusColor === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                        }`} />

                        {/* Standard name and version */}
                        <div className="mb-3 pr-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <a
                                href={standard.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-semibold text-gray-900 hover:text-blue-600 text-sm transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {standard.name}
                              </a>
                              {/* Help icon with detailed tooltip */}
                              <div className="relative group inline-block">
                                <button
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                  <Info className="w-3.5 h-3.5" />
                                </button>
                                {/* Detailed Tooltip - with improved positioning */}
                                <div className="absolute left-0 top-full mt-2 w-80 max-w-[90vw] bg-white border-2 border-blue-200 rounded-lg shadow-2xl p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none group-hover:pointer-events-auto z-[100]">
                                  <div className="font-semibold mb-2 text-sm text-blue-900">{standard.name}</div>
                                  <div className="text-gray-700 leading-relaxed space-y-2">
                                    {(key === 'owasp' || key === 'OWASP') && (
                                      <p><strong className="text-blue-700">Focus:</strong> Injection flaws, broken authentication, XSS, security misconfigurations, vulnerable components, access control.</p>
                                    )}
                                    {(key === 'cwe' || key === 'CWE') && (
                                      <p><strong className="text-blue-700">Focus:</strong> Buffer overflows, SQL injection, XSS, command injection, path traversal, memory safety issues.</p>
                                    )}
                                    {(key === 'pci' || key === 'PCI_DSS') && (
                                      <p><strong className="text-blue-700">Focus:</strong> Network security, cardholder data protection, encryption, access controls, security monitoring.</p>
                                    )}
                                    {(key === 'iso27001' || key === 'ISO_27001') && (
                                      <p><strong className="text-blue-700">Focus:</strong> Risk assessment, access control, cryptography, operations security, communications security, compliance.</p>
                                    )}
                                    {(key === 'gdpr' || key === 'GDPR') && (
                                      <p><strong className="text-blue-700">Focus:</strong> Data protection by design, consent management, breach notification, encryption, privacy rights.</p>
                                    )}
                                    {(key === 'soc2' || key === 'SOC2') && (
                                      <p><strong className="text-blue-700">Focus:</strong> Security controls, system availability, processing integrity, confidentiality, privacy protections.</p>
                                    )}
                                    {(key === 'nist' || key === 'NIST') && (
                                      <p><strong className="text-blue-700">Focus:</strong> Asset identification, protective controls, threat detection, incident response, recovery planning.</p>
                                    )}
                                  </div>
                                  {/* Tooltip arrow */}
                                  <div className="absolute left-4 -top-1.5 w-3 h-3 bg-white border-t-2 border-l-2 border-blue-200 transform rotate-45"></div>
                                </div>
                              </div>
                            </div>
                            {/* Expand/Collapse Button */}
                            <button
                              onClick={toggleExpand}
                              className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-600 cursor-pointer"
                            >
                              <span>{isExpanded ? 'Collapse' : 'Expand'}</span>
                              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </button>
                          </div>
                          <span className="text-xs text-gray-500">{standard.version}</span>
                        </div>

                        {/* Circular progress indicator */}
                        <div className="flex items-center gap-4 mb-3">
                          <div className="relative w-14 h-14">
                            <svg className="w-14 h-14 transform -rotate-90">
                              <circle
                                cx="28"
                                cy="28"
                                r="24"
                                stroke="currentColor"
                                strokeWidth="4"
                                fill="none"
                                className="text-gray-200"
                              />
                              <circle
                                cx="28"
                                cy="28"
                                r="24"
                                stroke="currentColor"
                                strokeWidth="4"
                                fill="none"
                                strokeDasharray={`${score * 1.51} 151`}
                                strokeLinecap="round"
                                className={
                                  statusColor === 'green' ? 'text-green-500' :
                                  statusColor === 'yellow' ? 'text-yellow-500' : 'text-red-500'
                                }
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className={`text-sm font-bold ${
                                statusColor === 'green' ? 'text-green-600' :
                                statusColor === 'yellow' ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {Math.round(score)}%
                              </span>
                            </div>
                          </div>

                          {/* Status badge */}
                          <div>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              compliance?.status === 'compliant' ? 'bg-green-100 text-green-800' :
                              compliance?.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {compliance?.status === 'compliant' ? <CheckCircle className="w-3 h-3" /> :
                               compliance?.status === 'partial' ? <AlertCircle className="w-3 h-3" /> :
                               <XCircle className="w-3 h-3" />}
                              {compliance?.status === 'compliant' ? 'Compliant' :
                               compliance?.status === 'partial' ? 'Partial' : 'Non-Compliant'}
                            </span>
                          </div>
                        </div>

                        {/* Summary */}
                        <p className="text-xs text-gray-600">
                          {compliance?.notes || 'Click to view detailed breakdown'}
                        </p>
                      </div>

                      {/* Expandable Details Section */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 bg-white/80 p-4">
                          <div className="space-y-2">
                            {compliance?.details && compliance.details.length > 0 ? (
                              compliance.details.map((detail: any, idx: number) => {
                                const detailStatus = detail.status || detail.result;
                                const detailColor = detailStatus === 'pass' || detailStatus === 'compliant' ? 'green' :
                                                   detailStatus === 'fail' || detailStatus === 'non-compliant' ? 'red' : 'yellow';
                                const hasFindings = detail.count !== undefined && detail.count > 0;
                                return (
                                  <div
                                    key={idx}
                                    onClick={(e) => {
                                      if (hasFindings) {
                                        e.stopPropagation();
                                        filterByStandard(key, detail.id || detail.code);
                                      }
                                    }}
                                    className={`flex items-center justify-between p-2 bg-white rounded border border-gray-100 transition-colors ${
                                      hasFindings ? 'hover:border-blue-400 hover:bg-blue-50 cursor-pointer' : 'hover:border-gray-300'
                                    }`}
                                    title={hasFindings ? `Click to view ${detail.count} finding${detail.count !== 1 ? 's' : ''}` : ''}
                                  >
                                    <div className="flex items-center gap-2 flex-1">
                                      <div className={`w-1.5 h-1.5 rounded-full ${
                                        detailColor === 'green' ? 'bg-green-500' :
                                        detailColor === 'red' ? 'bg-red-500' : 'bg-yellow-500'
                                      }`} />
                                      <span className="text-xs font-medium text-gray-700">
                                        {detail.id || detail.code}: {detail.name || detail.description}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {detail.count !== undefined && (
                                        <span className={`text-xs font-semibold ${hasFindings ? 'text-blue-600' : 'text-gray-500'}`}>
                                          {detail.count}
                                        </span>
                                      )}
                                      {detailStatus === 'pass' || detailStatus === 'compliant' ? (
                                        <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                      ) : detailStatus === 'fail' || detailStatus === 'non-compliant' ? (
                                        <XCircle className="w-3.5 h-3.5 text-red-600" />
                                      ) : (
                                        <AlertCircle className="w-3.5 h-3.5 text-yellow-600" />
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="text-center py-4">
                                <p className="text-xs text-gray-500">No detailed breakdown available</p>
                                <a
                                  href={standard.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 mt-2"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View full standard documentation
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Findings Tab */}
      {activeTab === 'results' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filters:</span>
              </div>
              <select
                value={filters.severity}
                onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-electric-blue"
              >
                <option value="">All Severities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
                <option value="info">Info</option>
              </select>
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-electric-blue"
              >
                <option value="">All Categories</option>
                <option value="sast">SAST</option>
                <option value="secrets">Secrets</option>
                <option value="dependency">Dependencies</option>
                <option value="container">Container</option>
                <option value="infrastructure">Infrastructure</option>
                <option value="license">License</option>
              </select>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-electric-blue"
              >
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="fixed">Fixed</option>
                <option value="ignored">Ignored</option>
                <option value="false_positive">False Positive</option>
              </select>
              {selectedScanId && (
                <button
                  onClick={() => fetchScanResults(selectedScanId)}
                  className="ml-auto flex items-center gap-2 px-3 py-1.5 text-sm text-electric-blue hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              )}
            </div>
          </div>

          {/* Active Standard Filter Indicator */}
          {filters.standard && complianceData?.standards[filters.standard] && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <Filter className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">
                    Filtering by:
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800 border border-blue-300">
                    {complianceData.standards[filters.standard].name}
                  </span>
                  {filters.categoryCode && (
                    <>
                      <span className="text-blue-600">→</span>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium bg-blue-200 text-blue-900 border border-blue-400">
                        {filters.categoryCode}
                      </span>
                    </>
                  )}
                  <span className="text-sm text-blue-700">
                    ({filteredScanResults.length} {filteredScanResults.length === 1 ? 'finding' : 'findings'})
                  </span>
                </div>
                <button
                  onClick={clearStandardFilter}
                  className="flex items-center gap-1 px-2 py-1 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded transition-colors"
                  title="Clear filter"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Bulk Action Bar */}
          {filteredScanResults.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedResults.size === filteredScanResults.length && filteredScanResults.length > 0}
                    onChange={selectAllResults}
                    className="w-4 h-4 text-electric-blue border-gray-300 rounded focus:ring-electric-blue"
                  />
                  <span className="text-sm text-gray-700">
                    {selectedResults.size === filteredScanResults.length ? 'Deselect All' : 'Select All'}
                  </span>
                </label>
                {selectedResults.size > 0 && (
                  <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {selectedResults.size} selected
                  </span>
                )}
              </div>

              {selectedResults.size > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-600">Bulk Update:</span>
                  <button
                    onClick={() => bulkUpdateResultStatus('open')}
                    disabled={bulkUpdating}
                    className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    Open
                  </button>
                  <button
                    onClick={() => bulkUpdateResultStatus('in_progress')}
                    disabled={bulkUpdating}
                    className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    In Progress
                  </button>
                  <button
                    onClick={() => bulkUpdateResultStatus('fixed')}
                    disabled={bulkUpdating}
                    className="px-3 py-1.5 text-xs font-medium bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
                  >
                    Fixed
                  </button>
                  <button
                    onClick={() => bulkUpdateResultStatus('ignored')}
                    disabled={bulkUpdating}
                    className="px-3 py-1.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors disabled:opacity-50"
                  >
                    Ignored
                  </button>
                  <button
                    onClick={() => bulkUpdateResultStatus('false_positive')}
                    disabled={bulkUpdating}
                    className="px-3 py-1.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50"
                  >
                    False Positive
                  </button>
                  {bulkUpdating && (
                    <Loader2 className="w-4 h-4 animate-spin text-electric-blue" />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Results List */}
          {resultsLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-electric-blue" />
            </div>
          ) : filteredScanResults.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No findings match your filters</h3>
              <p className="text-gray-500 mt-2">Try adjusting your filters or run a new scan.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredScanResults.map((result) => (
                <div key={result.id} className={`bg-white rounded-lg shadow overflow-hidden ${selectedResults.has(result.id) ? 'ring-2 ring-electric-blue' : ''}`}>
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedResult(expandedResult === result.id ? null : result.id)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Checkbox for bulk selection */}
                      <div className="flex-shrink-0 mt-1">
                        <input
                          type="checkbox"
                          checked={selectedResults.has(result.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleResultSelection(result.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 text-electric-blue border-gray-300 rounded focus:ring-electric-blue cursor-pointer"
                        />
                      </div>
                      <div className="flex-shrink-0 mt-1">
                        {getCategoryIcon(result.category)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <SeverityBadge severity={result.severity} />
                          <StatusBadge status={result.status} />
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {result.tool}
                          </span>
                          {result.cve_id && (
                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                              {result.cve_id}
                            </span>
                          )}
                        </div>
                        <h4 className="font-medium text-gray-900 mt-2 line-clamp-1">{result.title}</h4>
                        {result.file_path && (
                          <p className="text-sm text-gray-500 mt-1 font-mono truncate">
                            {result.file_path}
                            {result.line_number && `:${result.line_number}`}
                          </p>
                        )}
                        {result.package_name && (
                          <p className="text-sm text-gray-500 mt-1">
                            Package: {result.package_name}
                            {result.package_version && `@${result.package_version}`}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {expandedResult === result.id ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedResult === result.id && (
                    <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-4">
                      {result.description && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-1">Description</h5>
                          <p className="text-sm text-gray-600">{result.description}</p>
                        </div>
                      )}

                      {/* Impacts Section */}
                      {result.standards_impacted && result.standards_impacted.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Impacts</h5>
                          <div className="flex flex-wrap gap-2">
                            {result.standards_impacted.map((impact, index) => (
                              <ImpactBadge key={index} impact={impact} />
                            ))}
                          </div>
                        </div>
                      )}

                      {result.remediation && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-1">Remediation</h5>
                          <p className="text-sm text-gray-600">{result.remediation}</p>
                        </div>
                      )}

                      {result.remediation_command && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-1">Fix Command</h5>
                          <div className="flex items-center gap-2 bg-gray-900 text-green-400 p-3 rounded-lg font-mono text-sm">
                            <code className="flex-1">{result.remediation_command}</code>
                            <button
                              onClick={() => copyCommand(result.remediation_command!, result.id)}
                              className="p-1 hover:bg-gray-700 rounded transition-colors"
                              title="Copy command"
                            >
                              {copiedCommand === result.id ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}

                      {result.fixed_version && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-1">Fixed Version</h5>
                          <p className="text-sm text-green-600 font-medium">{result.fixed_version}</p>
                        </div>
                      )}

                      {result.references && result.references.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-1">References</h5>
                          <div className="flex flex-wrap gap-2">
                            {result.references.slice(0, 3).map((ref, index) => (
                              <a
                                key={index}
                                href={ref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                              >
                                <ExternalLink className="w-3 h-3" />
                                {ref.length > 50 ? ref.substring(0, 50) + '...' : ref}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Status Actions */}
                      <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                        <span className="text-sm text-gray-500">Update Status:</span>
                        {['open', 'in_progress', 'fixed', 'ignored', 'false_positive'].map((status) => (
                          <button
                            key={status}
                            onClick={() => updateResultStatus(result.id, status)}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              result.status === status
                                ? 'bg-super-dark-purple text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {status.replace('_', ' ')}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Findings</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {dashboardData?.recentScans?.map((scan) => (
                <tr key={scan.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {formatDate(scan.completed_at || scan.started_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 capitalize">{scan.scan_type}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={scan.status} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{scan.total_findings}</span>
                      {scan.critical_count > 0 && (
                        <span className="text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                          {scan.critical_count} critical
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-blue-700 ${
                        scan.security_score >= 90 ? 'bg-green-500' :
                        scan.security_score >= 80 ? 'bg-green-400' :
                        scan.security_score >= 70 ? 'bg-yellow-500' :
                        scan.security_score >= 60 ? 'bg-yellow-600' :
                        scan.security_score >= 50 ? 'bg-orange-500' :
                        'bg-red-500'
                      }`}>
                        {scan.security_score >= 90 ? 'A' : scan.security_score >= 80 ? 'B' : scan.security_score >= 70 ? 'C' : scan.security_score >= 60 ? 'D' : 'F'}
                      </span>
                      <span className={`text-sm font-medium ${
                        scan.security_score >= 80 ? 'text-green-600' :
                        scan.security_score >= 60 ? 'text-yellow-600' :
                        scan.security_score >= 40 ? 'text-orange-600' :
                        'text-red-600'
                      }`}>
                        {(Number(scan.security_score) || 0).toFixed(1)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDuration(scan.scan_duration_seconds)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedScanId(scan.id);
                          setActiveTab('results');
                        }}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                        title="View Results"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <div className="relative group">
                        <button
                          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                          title="Download Report"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                          <button
                            onClick={() => exportReportJSON(scan.id)}
                            className="w-full px-3 py-1.5 text-xs text-left text-gray-700 hover:bg-gray-100"
                          >
                            Export JSON
                          </button>
                          <button
                            onClick={() => exportReportPDF(scan.id)}
                            className="w-full px-3 py-1.5 text-xs text-left text-gray-700 hover:bg-gray-100"
                          >
                            Export PDF
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!dashboardData?.recentScans || dashboardData.recentScans.length === 0) && (
            <div className="p-8 text-center text-gray-500">
              No scan history available. Run your first scan to get started.
            </div>
          )}
        </div>
      )}

      {/* Tools Tab - Comprehensive Documentation */}
      {activeTab === 'tools' && (
        <div className="space-y-6">
          {/* Methodology Overview */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h2 className="text-xl font-semibold mb-3 flex items-center gap-2 text-gray-900">
              <Shield className="w-6 h-6 text-gray-700" />
              Security Scanning Methodology
            </h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Security Scanner Security Scanner performs comprehensive security analysis using industry-leading open-source tools.
              Our methodology follows a defense-in-depth approach, examining multiple security layers to provide thorough coverage.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-4">
              {[
                { step: 1, title: 'SAST', Icon: FileSearch },
                { step: 2, title: 'Secrets', Icon: Key },
                { step: 3, title: 'Dependencies', Icon: Package },
                { step: 4, title: 'Container', Icon: Server },
                { step: 5, title: 'IaC', Icon: Settings },
                { step: 6, title: 'License', Icon: FileText }
              ].map(item => (
                <div key={item.step} className="bg-white rounded-lg p-4 text-center border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all">
                  <div className="flex justify-center mb-2">
                    <item.Icon className="w-8 h-8 text-gray-600" />
                  </div>
                  <div className="text-xs font-medium text-gray-500 mb-1">Step {item.step}</div>
                  <div className="text-sm font-semibold text-gray-900">{item.title}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Scoring Formula */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-600" />
              Security Score Calculation
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <code className="text-lg font-mono text-indigo-600">
                Security Score = (1 - (Issues Found / Total Criteria Evaluated)) × 100
              </code>
            </div>
            <p className="text-gray-600 mb-4">
              The security score is calculated objectively based on the ratio of issues found to the total security criteria evaluated.
              This provides a transparent, repeatable metric that can be tracked over time.
            </p>
            <div className="grid grid-cols-5 gap-2">
              {[
                { grade: 'A', range: '90-100', color: 'bg-green-500', desc: 'Excellent' },
                { grade: 'B', range: '80-89', color: 'bg-lime-500', desc: 'Good' },
                { grade: 'C', range: '70-79', color: 'bg-yellow-500', desc: 'Fair' },
                { grade: 'D', range: '60-69', color: 'bg-orange-500', desc: 'Poor' },
                { grade: 'F', range: '0-59', color: 'bg-red-500', desc: 'Critical' }
              ].map(g => (
                <div key={g.grade} className="text-center">
                  <div className={`${g.color} text-blue-700 text-xl font-bold rounded-lg py-2 mb-1`}>{g.grade}</div>
                  <div className="text-xs text-gray-500">{g.range}</div>
                  <div className="text-xs font-medium text-gray-700">{g.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tools Documentation Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {dashboardData?.tools && Object.entries(dashboardData.tools).map(([key, tool]) => (
              <div key={key} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Tool Header */}
                <div className="bg-gray-50 border-b border-gray-100 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        {getCategoryIcon(tool.category)}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 flex items-center gap-2">
                          {tool.name}
                          {tool.url && (
                            <a
                              href={tool.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-800"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </h3>
                        <p className="text-xs text-gray-500">{tool.vendor || 'Open Source'}</p>
                      </div>
                    </div>
                    {tool.isInstalled ? (
                      <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full">
                        <CheckCircle className="w-4 h-4" />
                        Installed
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 text-sm rounded-full">
                        <XCircle className="w-4 h-4" />
                        Not Installed
                      </span>
                    )}
                  </div>
                </div>

                {/* Tool Content */}
                <div className="p-4 space-y-4">
                  {/* Description */}
                  <div>
                    <p className="text-sm text-gray-600">
                      {tool.detailedDescription || tool.description}
                    </p>
                  </div>

                  {/* What It Checks */}
                  {tool.whatItChecks && tool.whatItChecks.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1">
                        <Eye className="w-4 h-4 text-indigo-500" />
                        What It Checks
                      </h4>
                      <ul className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                        {tool.whatItChecks.slice(0, 6).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-1">
                            <span className="text-indigo-500 mt-0.5">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                      {tool.whatItChecks.length > 6 && (
                        <p className="text-xs text-gray-400 mt-1">+{tool.whatItChecks.length - 6} more checks</p>
                      )}
                    </div>
                  )}

                  {/* How It Works */}
                  {tool.howItWorks && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-1">
                        <Settings className="w-4 h-4 text-indigo-500" />
                        How It Works
                      </h4>
                      <p className="text-xs text-gray-600">{tool.howItWorks}</p>
                    </div>
                  )}

                  {/* Standards */}
                  {tool.standards && tool.standards.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Compliance Standards</h4>
                      <div className="flex flex-wrap gap-1">
                        {tool.standards.map((std, idx) => (
                          <span key={idx} className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full">
                            {std}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Technical Details */}
                  <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
                    <span>Command: <code className="bg-gray-100 px-1 rounded">{tool.command}</code></span>
                    {tool.version && <span>v{tool.version}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
              <Info className="w-5 h-5" />
              Important Information for Security Evaluators
            </h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
              <div>
                <h4 className="font-semibold mb-1">Data Retention</h4>
                <p>Scan results are stored securely and retained for compliance audit purposes. No source code is stored - only metadata and findings.</p>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Recommended Frequency</h4>
                <p>We recommend running security scans on every code commit, at minimum weekly, and before any production deployment.</p>
              </div>
              <div>
                <h4 className="font-semibold mb-1">False Positives</h4>
                <p>Findings can be marked as false positives after review. All status changes are tracked in the audit log.</p>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Compliance Mapping</h4>
                <p>Findings are mapped to OWASP Top 10, CWE, PCI DSS, ISO 27001, GDPR, SOC 2, and NIST CSF standards.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Data State */}
      {!dashboardData?.latestScan && activeTab === 'overview' && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">No Security Scans Yet</h3>
          <p className="text-gray-500 mb-6">
            Run your first security scan to analyze your codebase for vulnerabilities.
          </p>
          <button
            onClick={startScan}
            disabled={scanning}
            className="inline-flex items-center gap-2 px-6 py-3 bg-super-dark-purple text-white font-medium rounded-lg hover:bg-electric-blue transition-colors"
          >
            <Play className="w-5 h-5" />
            Run First Scan
          </button>
        </div>
      )}
    </div>
  );
};

export default SecurityScanDashboard;
